import { confirmInGrain } from "../utils/grainDialogs.js";
import React from "react";
import {
  Archive,
  AlertTriangle,
  History,
  Info,
  KeyRound,
  LoaderCircle,
  RefreshCcw,
  Search,
  Shield,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { startPreferencesTarget } from "../utils/preferencesDelegation.js";

import {
  DATA_MANAGER_EXPORT_PERMISSION_KEYS,
  canAccessAccessManagement,
  canReadAccessAudit,
  canManageAccessUsers,
  canReadAccessCatalog,
} from "../lib/accessControl.js";
import {
  archiveAccessRole,
  assignAccessUserRole,
  createAccessUser,
  createAccessRole,
  deleteAccessUser,
  fetchAccessAuditEvents,
  fetchAccessPermissionCatalog,
  fetchAccessRoles,
  fetchAccessUsers,
  reactivateAccessUser,
  sendAccessUserPasswordResetEmail,
  formatPasswordResetEmailError,
  replaceAccessRolePermissions,
  suspendAccessUser,
  updateAccessRole,
  updateAccessUserPassword,
  updateAccessUserRole,
} from "../lib/accessAdminApi.js";
import useAccess from "../hooks/useAccess.jsx";
import {
  buttonSizeClasses,
  buttonToneClasses,
  joinClasses,
  pillToneClasses,
  statusToneClasses,
} from "./resultsPrimitives/shared.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import SystemConfigAccessHelpDialog from "./SystemConfigAccessHelpDialog.jsx";

const FEEDBACK_TONE_CLASS_NAMES = {
  danger: statusToneClasses.danger,
  info: statusToneClasses.info,
  success: statusToneClasses.success,
};
const SHARED_IDENTITY_CONFIRMATION = {
  key: "shared-supabase-identity",
  label: "Je comprends que cette suppression retire l’identité Supabase partagée et que l’autre environnement sera nettoyé automatiquement dès qu’il est actif.",
  description: "Le local et Railway utilisent le même annuaire Supabase Auth et réconcilient les suppressions.",
  required: true,
};
const PRIMARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.primary,
]);
const SECONDARY_ACTION_CLASS_NAME = joinClasses([
  "inline-flex items-center justify-center gap-2 rounded-[4px] border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonSizeClasses.sm,
  buttonToneClasses.ghost,
]);
const ACCESS_FORM_CONTROL_CLASS_NAME = joinClasses([
  "min-w-0 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-2 text-[0.84rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
]);
const ACCESS_ROLE_HEADER_PILL_CLASS_NAME = "inline-flex h-[1.125rem] items-center rounded-[4px] px-1.5 text-[0.56rem] font-bold uppercase leading-none tracking-[0.1em]";
const DRIVER_ROLE_KEY = "driver";
const PROTECTED_ACCESS_ADMIN_EMAIL = "pascalgilbert00@gmail.com";
const ACCESS_TABLE_ACTION_BASE_CLASS_NAME = "inline-flex h-8 items-center justify-center gap-1.5 rounded-[4px] border px-2.5 text-[0.72rem] font-semibold whitespace-nowrap transition disabled:cursor-not-allowed disabled:opacity-60";
const ACCESS_TABLE_PRIMARY_ACTION_CLASS_NAME = joinClasses([
  ACCESS_TABLE_ACTION_BASE_CLASS_NAME,
  buttonToneClasses.primary,
]);
const ACCESS_TABLE_SECONDARY_ACTION_CLASS_NAME = joinClasses([
  ACCESS_TABLE_ACTION_BASE_CLASS_NAME,
  buttonToneClasses.ghost,
]);
const ACCESS_TABLE_DELETE_ACTION_CLASS_NAME = joinClasses([
  ACCESS_TABLE_ACTION_BASE_CLASS_NAME,
  "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100",
]);
const ACCESS_TABLE_SUSPEND_ACTION_CLASS_NAME = joinClasses([
  ACCESS_TABLE_ACTION_BASE_CLASS_NAME,
  "border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300 hover:bg-amber-100",
]);
const ACCESS_TABLE_REACTIVATE_ACTION_CLASS_NAME = joinClasses([
  ACCESS_TABLE_ACTION_BASE_CLASS_NAME,
  "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong",
]);
const ACCESS_TABLE_ROLE_SELECT_CLASS_NAME = joinClasses([
  "rounded-[4px] border border-slate-200 bg-white px-3 py-1 text-[0.76rem] font-semibold text-slate-900 outline-hidden transition focus:border-border-accent-strong focus:ring-2 focus:ring-border-accent-subtle disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
  "h-8 min-h-8 w-[12rem] min-w-[12rem] max-w-[12rem] flex-none",
]);
const LINKED_ROLE_PERMISSION_KEY_GROUPS = [
  [
    "system.settings.here_routing.write",
    "system.settings.traffic_routing.write",
  ],
];
const ROLE_EDITOR_PERMISSION_SCOPE_BUNDLES = [
  {
    label: "Trafic / horaire HERE et amplification",
    scope: "system_here_routing_and_traffic",
    scope_label: "system_here_routing + system_traffic_routing",
    scopes: ["system_here_routing", "system_traffic_routing"],
  },
];
const ROLE_EDITOR_WIDE_LAYOUT_SCOPES = new Set([
  "system_solver_low_load",
]);
const DATA_MANAGER_SCOPE = "data_manager";
const DATA_MANAGER_TABLE_PERMISSION_KEYS = [
  "data_manager.orders.write",
  "data_manager.trucks.write",
  "data_manager.compartments.write",
  "data_manager.employees.write",
  "data_manager.client_correspondences.write",
  "data_manager.products.write",
];
const SYSTEM_ROLE_DISPLAY_ORDER = ["admin", "operations_manager", "billing", "driver", "user"];
const SYSTEM_ROLE_KEY_SET = new Set(SYSTEM_ROLE_DISPLAY_ORDER);
const PERMISSION_FAMILY_DEFINITIONS = [
  {
    description: "Imports, camions, commandes et correspondances client.",
    eyebrow: "Données",
    key: "data_manager",
    title: "Data Manager",
  },
  {
    description: "Relevés chauffeurs, préchargements, travail non facturable et validation des répartitions.",
    eyebrow: "Opérations",
    key: "consolidation",
    title: "Consolidation",
  },
  {
    description: "Relevés de transport, entreprises de facturation et paramètres.",
    eyebrow: "Facturation",
    key: "billing",
    title: "Facturation",
  },
  {
    description: "Les valeurs se modifient dans Paramètres système ; ici vous accordez les droits d’édition par réglage.",
    eyebrow: "Système",
    key: "system_settings",
    title: "Paramètres système",
  },
  {
    description: "Lancer, clore, sauvegarder et restaurer les journées dispatch.",
    eyebrow: "Opérations",
    key: "dispatch",
    title: "Dispatch",
  },
  {
    description: "Consultation des trajets réels, suivi en direct et associations des boîtiers aux camions.",
    eyebrow: "Opérations",
    key: "geotab",
    title: "Geotab",
  },
  {
    description: "Imports Commandes, Camions, Remorques, Chauffeurs, Clients et Produits.",
    eyebrow: "Données",
    key: "imports",
    title: "Imports",
  },
  {
    description: "Gestion des fichiers source par jeu de données.",
    eyebrow: "Données",
    key: "source_files",
    title: "Fichiers source",
  },
  {
    description: "Accès aux rapports et exports.",
    eyebrow: "Rapports",
    key: "reports",
    title: "Rapports",
  },
  {
    description: "Gestion des utilisateurs, rôles et permissions d’administration.",
    eyebrow: "RBAC",
    key: "access",
    title: "Accès",
  },
  {
    description: "Comptes chauffeurs, accès au portail terrain et publication des tournées.",
    eyebrow: "Chauffeurs",
    key: "portal",
    title: "Portail",
  },
  {
    description: "Lecture des journaux d’accès et des actions sensibles.",
    eyebrow: "Traçabilité",
    key: "audit",
    title: "Audit",
  },
];

function formatDateTime(value) {
  if (!value) {
    return "—";
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
}

function resolveFeedbackToneClassName(tone) {
  return FEEDBACK_TONE_CLASS_NAMES[tone] || statusToneClasses.neutral;
}

function buildRoleDraftMap(users) {
  if (!Array.isArray(users)) {
    return {};
  }

  return users.reduce((nextMap, user) => {
    const roleKey = resolveAccessUserRoleKey(user);
    if (user?.user_id && roleKey) {
      nextMap[user.user_id] = roleKey;
    }
    return nextMap;
  }, {});
}

function resolveAccessRoleKey(roleKey, roles) {
  if (roleKey && roles.some((role) => role.role_key === roleKey)) {
    return roleKey;
  }

  return roles[0]?.role_key || "";
}

function buildManualRoleAssignmentState(roleKey = "") {
  return {
    userId: "",
    email: "",
    roleKey,
  };
}

function buildAccessUserCreationState(roleKey = "") {
  return {
    fullName: "",
    email: "",
    password: "",
    roleKey,
  };
}

function resolveRoleBadgeClassName(roleKey) {
  return roleKey === "admin" ? pillToneClasses.info : pillToneClasses.outline;
}

function resolveLinkedRolePermissionKeys(permissionKey) {
  return LINKED_ROLE_PERMISSION_KEY_GROUPS.find((group) => group.includes(permissionKey)) || null;
}

function normalizeRolePermissionKeys(permissionKeys) {
  const nextPermissionKeySet = new Set(Array.isArray(permissionKeys) ? permissionKeys.filter(Boolean) : []);

  LINKED_ROLE_PERMISSION_KEY_GROUPS.forEach((group) => {
    if (group.some((permissionKey) => nextPermissionKeySet.has(permissionKey))) {
      group.forEach((permissionKey) => nextPermissionKeySet.add(permissionKey));
    }
  });

  return Array.from(nextPermissionKeySet).sort();
}

function buildRoleEditorPermissionGroups(groups) {
  if (!Array.isArray(groups)) {
    return [];
  }

  const groupsByScope = new Map(groups.map((group) => [group?.scope, group]));
  const consumedScopes = new Set();

  return groups.reduce((nextGroups, group) => {
    if (!group || typeof group !== "object" || consumedScopes.has(group.scope)) {
      return nextGroups;
    }

    const bundle = ROLE_EDITOR_PERMISSION_SCOPE_BUNDLES.find((candidate) => candidate.scopes.includes(group.scope));
    if (!bundle) {
      nextGroups.push(group);
      return nextGroups;
    }

    const bundledGroups = bundle.scopes
      .map((scope) => groupsByScope.get(scope))
      .filter((candidate) => candidate && typeof candidate === "object");
    const seenPermissionKeys = new Set();
    const bundledPermissions = [];

    bundledGroups.forEach((candidate) => {
      consumedScopes.add(candidate.scope);
      (Array.isArray(candidate.permissions) ? candidate.permissions : []).forEach((permission) => {
        const permissionKey = permission?.permission_key;
        if (!permissionKey || seenPermissionKeys.has(permissionKey)) {
          return;
        }

        seenPermissionKeys.add(permissionKey);
        bundledPermissions.push(permission);
      });
    });

    if (bundledPermissions.length === 0) {
      return nextGroups;
    }

    nextGroups.push({
      ...group,
      label: bundle.label,
      permissions: bundledPermissions,
      scope: bundle.scope,
      scope_label: bundle.scope_label,
    });
    return nextGroups;
  }, []);
}

const PORTAL_PERMISSION_SCOPES = new Set([
  "access_driver_accounts",
  "access_driver_portal",
  "portal_publications",
]);

function resolvePermissionGroupFamilyKey(group) {
  const scope = String(group?.scope || "").toLowerCase();
  const permissionKeys = Array.isArray(group?.permissions)
    ? group.permissions.map((permission) => String(permission?.permission_key || "").toLowerCase()).filter(Boolean)
    : [];

  if (scope.startsWith("data_manager") || permissionKeys.some((permissionKey) => permissionKey.startsWith("data."))) {
    return "data_manager";
  }

  if (scope === "billing" || permissionKeys.some((permissionKey) => permissionKey.startsWith("billing."))) {
    if (permissionKeys.length > 0 && permissionKeys.every((permissionKey) => permissionKey.startsWith("billing.consolidation."))) {
      return "consolidation";
    }
    return "billing";
  }

  if (scope.startsWith("system_") || permissionKeys.some((permissionKey) => permissionKey.startsWith("system."))) {
    return "system_settings";
  }

  if (
    PORTAL_PERMISSION_SCOPES.has(scope)
    || permissionKeys.some((permissionKey) => permissionKey.startsWith("driver."))
    || permissionKeys.includes("dispatch.day.publish_portal")
  ) {
    return "portal";
  }

  if (scope.startsWith("access_") || permissionKeys.some((permissionKey) => permissionKey.startsWith("access."))) {
    return "access";
  }

  if (scope.startsWith("audit_") || permissionKeys.some((permissionKey) => permissionKey.startsWith("audit."))) {
    return "audit";
  }

  if (scope === "dispatch" || permissionKeys.some((permissionKey) => permissionKey.startsWith("dispatch."))) {
    return "dispatch";
  }

  if (scope === "imports" || permissionKeys.some((permissionKey) => permissionKey.startsWith("imports."))) {
    return "imports";
  }

  if (
    scope === "source_files"
    || permissionKeys.some((permissionKey) => permissionKey.startsWith("source_files.") || permissionKey.startsWith("source_file."))
  ) {
    return "source_files";
  }

  if (scope === "reports" || permissionKeys.some((permissionKey) => permissionKey.startsWith("reports."))) {
    return "reports";
  }

  return scope || "misc";
}

function buildPermissionFamilies(groups) {
  if (!Array.isArray(groups) || groups.length === 0) {
    return [];
  }

  const families = new Map(
    PERMISSION_FAMILY_DEFINITIONS.map((family) => [family.key, { ...family, groups: [], permissionCount: 0 }]),
  );
  const definedFamilyKeys = PERMISSION_FAMILY_DEFINITIONS.map((family) => family.key);

  const displayGroups = groups.flatMap((group) => {
    const permissions = Array.isArray(group?.permissions) ? group.permissions : [];
    const consolidationPermissions = permissions.filter((permission) => permission.permission_key?.startsWith("billing.consolidation."));
    const geotabPermissions = permissions.filter((permission) => permission.permission_key?.startsWith("geotab."));
    if (consolidationPermissions.length === 0 && geotabPermissions.length === 0) {
      return [group];
    }
    const otherPermissions = permissions.filter((permission) =>
      !permission.permission_key?.startsWith("billing.consolidation.") && !permission.permission_key?.startsWith("geotab."));
    return [
      ...(otherPermissions.length > 0 ? [{ ...group, permissions: otherPermissions }] : []),
      ...(consolidationPermissions.length > 0 ? [{ ...group, scope: "consolidation", label: "Consolidation", permissions: consolidationPermissions }] : []),
      ...(geotabPermissions.length > 0 ? [{ ...group, scope: "geotab", label: "Geotab", permissions: geotabPermissions }] : []),
    ];
  });

  displayGroups.forEach((group) => {
    const familyKey = resolvePermissionGroupFamilyKey(group);
    let family = families.get(familyKey);

    if (!family) {
      family = {
        description: group?.label || "",
        eyebrow: "Autres",
        key: familyKey,
        title: group?.label || familyKey.replace(/_/g, " "),
        groups: [],
        permissionCount: 0,
      };
      families.set(familyKey, family);
    }

    family.groups.push(group);
    family.permissionCount += Array.isArray(group?.permissions) ? group.permissions.length : 0;
  });

  const orderedFamilies = definedFamilyKeys
    .map((familyKey) => families.get(familyKey))
    .filter((family) => family && family.groups.length > 0);
  const dynamicFamilies = Array.from(families.values())
    .filter((family) => !definedFamilyKeys.includes(family.key) && family.groups.length > 0);

  return [...orderedFamilies, ...dynamicFamilies];
}

function PermissionFamilyJumpNav({ families }) {
  if (!Array.isArray(families) || families.length < 2) {
    return null;
  }

  return (
    <nav aria-label="Accès rapide aux sections de permissions" className="flex flex-wrap gap-1.5 rounded-[4px] border border-slate-200 bg-white/80 px-2.5 py-2">
      <span className="self-center px-1 text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Aller à</span>
      {families.map((family) => (
        <a
          className="inline-flex h-7 items-center rounded-[4px] border border-slate-200 bg-white px-2.5 text-[0.7rem] font-semibold text-slate-700 transition hover:border-accent/40 hover:text-accent-strong"
          href={`#admin-permission-family-${family.key}`}
          key={family.key}
        >
          {family.title}
        </a>
      ))}
    </nav>
  );
}

function resolvePermissionGroupGridClassName(groupCount) {
  if (groupCount >= 2) {
    return "grid gap-1.5 min-[980px]:grid-cols-2";
  }
  return "grid gap-1.5";
}

function resolvePermissionGroupCardClassName(group) {
  const isWideLayout = ROLE_EDITOR_WIDE_LAYOUT_SCOPES.has(group?.scope);
  return joinClasses([
    "grid gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2 py-2 shadow-card-subtle min-w-0",
    isWideLayout ? "min-[1180px]:col-span-full" : "",
  ]);
}

function resolvePermissionItemsGridClassName(group) {
  return group.permissions.length > 1
    ? "grid gap-1.5 min-[720px]:grid-cols-2"
    : "grid gap-1.5";
}

function buildRoleEditorDraft(role) {
  return {
    description: role?.description || "",
    label: role?.label || "",
    permissionKeys: normalizeRolePermissionKeys(
      Array.isArray(role?.permissions)
        ? role.permissions.map((permission) => permission.permission_key).filter(Boolean)
        : [],
    ),
  };
}

function buildRoleCloneState(baseRoleKey = "") {
  return {
    baseRoleKey,
    description: "",
    label: "",
    roleKey: "",
  };
}

function slugifyRoleKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function compareStringArrays(leftValues, rightValues) {
  const left = Array.isArray(leftValues) ? [...leftValues].sort() : [];
  const right = Array.isArray(rightValues) ? [...rightValues].sort() : [];
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function isRoleDraftDirty(role, draft) {
  if (!role || !draft) {
    return false;
  }

  const currentPermissionKeys = normalizeRolePermissionKeys(
    Array.isArray(role.permissions)
      ? role.permissions.map((permission) => permission.permission_key).filter(Boolean)
      : [],
  );

  return role.label !== draft.label
    || (role.description || "") !== (draft.description || "")
    || !compareStringArrays(currentPermissionKeys, draft.permissionKeys);
}

function resolvePermissionSearchMatch(permission, normalizedSearch) {
  if (!normalizedSearch) {
    return true;
  }

  const haystack = [
    permission?.label,
    permission?.permission_key,
    permission?.description,
    permission?.scope_label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalizedSearch);
}

function isSensitivePermission(permission) {
  return permission?.is_sensitive === true;
}

function isDataManagerPermissionGroup(group) {
  return group?.scope === DATA_MANAGER_SCOPE;
}

function partitionDataManagerPermissions(permissions) {
  const tableKeySet = new Set(DATA_MANAGER_TABLE_PERMISSION_KEYS);
  const exportKeySet = new Set(DATA_MANAGER_EXPORT_PERMISSION_KEYS);
  const tablePermissions = [];
  const exportPermissions = [];
  const otherPermissions = [];

  (Array.isArray(permissions) ? permissions : []).forEach((permission) => {
    if (tableKeySet.has(permission?.permission_key)) {
      tablePermissions.push(permission);
      return;
    }

    if (exportKeySet.has(permission?.permission_key)) {
      exportPermissions.push(permission);
      return;
    }

    otherPermissions.push(permission);
  });

  tablePermissions.sort(
    (left, right) =>
      DATA_MANAGER_TABLE_PERMISSION_KEYS.indexOf(left.permission_key)
      - DATA_MANAGER_TABLE_PERMISSION_KEYS.indexOf(right.permission_key),
  );
  exportPermissions.sort(
    (left, right) =>
      DATA_MANAGER_EXPORT_PERMISSION_KEYS.indexOf(left.permission_key)
      - DATA_MANAGER_EXPORT_PERMISSION_KEYS.indexOf(right.permission_key),
  );
  return { exportPermissions, otherPermissions, tablePermissions };
}

function DataManagerPermissionSections({ permissions, renderPermission }) {
  const { exportPermissions, otherPermissions, tablePermissions } = partitionDataManagerPermissions(permissions);

  return (
    <div className="grid gap-1.5">
      {tablePermissions.length > 0 ? (
        <section className="grid gap-1.5 rounded-[4px] border border-slate-300 bg-slate-50/60 px-2 py-2">
          <div className="grid gap-0.5">
            <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">Accès aux tables</p>
            <p className="m-0 text-[0.74rem] leading-4.5 text-slate-600">Créer et modifier les données dans chaque table du Data Manager.</p>
          </div>
          <div className={resolvePermissionItemsGridClassName({ permissions: tablePermissions })}>
            {tablePermissions.map(renderPermission)}
          </div>
        </section>
      ) : null}
      {exportPermissions.length > 0 ? (
        <section className="grid gap-1.5 rounded-[4px] border border-slate-300 bg-slate-50/60 px-2 py-2">
          <div className="grid gap-0.5">
            <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">Accès aux exports</p>
            <p className="m-0 text-[0.74rem] leading-4.5 text-slate-600">Télécharger l&apos;export de chaque table depuis le panneau Exports et imports admin du Data Manager.</p>
          </div>
          <div className={resolvePermissionItemsGridClassName({ permissions: exportPermissions })}>
            {exportPermissions.map(renderPermission)}
          </div>
        </section>
      ) : null}
      {otherPermissions.length > 0 ? (
        <div className={resolvePermissionItemsGridClassName({ permissions: otherPermissions })}>
          {otherPermissions.map(renderPermission)}
        </div>
      ) : null}
    </div>
  );
}

function buildRoleSaveConfirmationModel({ role, nextLabel, nextDescription, currentPermissionKeys, nextPermissionKeys }) {
  const hasDefinitionChange = role.label !== nextLabel || (role.description || "") !== nextDescription;
  const addedPermissions = nextPermissionKeys.filter((permissionKey) => !currentPermissionKeys.includes(permissionKey));
  const removedPermissions = currentPermissionKeys.filter((permissionKey) => !nextPermissionKeys.includes(permissionKey));

  return {
    currentPermissionKeys,
    hasDefinitionChange,
    nextDescription,
    nextLabel,
    nextPermissionKeys,
    roleKey: role.role_key,
    confirmLabel: "Enregistrer",
    description: `Le rôle ${role.label} sera mis à jour et les accès seront rechargés après validation.`,
    impactItems: [
      hasDefinitionChange ? `Libellé ou description modifiés pour ${nextLabel}.` : null,
      addedPermissions.length > 0 ? `${addedPermissions.length} permission(s) ajoutée(s).` : null,
      removedPermissions.length > 0 ? `${removedPermissions.length} permission(s) retirée(s).` : null,
    ].filter(Boolean),
    summaryItems: [
      { label: "Rôle", value: nextLabel },
      { label: "Permissions", value: String(nextPermissionKeys.length) },
      { label: "Utilisateurs", value: String(role.active_user_count || 0) },
    ],
    title: `Enregistrer les changements du rôle ${role.label} ?`,
    tone: "accent",
  };
}

function buildAccessUserPasswordConfirmationModel(user) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";

  return {
    user,
    confirmLabel: "Définir le mot de passe",
    description: "Le backend admin Supabase remplacera immédiatement le mot de passe actuel.",
    impactItems: [],
    summaryItems: [
      { label: "Utilisateur", value: targetLabel },
      { label: "UID", value: user?.user_id || "—", span: "fill", nowrap: true },
    ],
    summaryDensity: "compact",
    textPrompt: {
      autoComplete: "new-password",
      description: "Minimum 8 caractères. Le mot de passe actuel sera écrasé dès validation.",
      key: "new-password",
      label: "Nouveau mot de passe",
      minLength: 8,
      placeholder: "Minimum 8 caractères",
      required: true,
      type: "password",
    },
    title: `Définir un nouveau mot de passe pour ${targetLabel} ?`,
    tone: "accent",
    warningMessage: "Pour laisser l’utilisateur choisir lui-même son mot de passe, utilisez plutôt le lien de réinitialisation.",
  };
}

function buildAccessUserPasswordResetEmailConfirmationModel(user) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";

  return {
    user,
    confirmLabel: "Envoyer le lien",
    description: "Un courriel TransIA sera envoyé avec un lien sécurisé vers la page /reset-password de l’application publique.",
    impactItems: [
      "L’utilisateur pourra choisir lui-même un nouveau mot de passe.",
      "Le lien est personnel et temporaire.",
    ],
    summaryItems: [
      { label: "Courriel", value: targetLabel },
      { label: "UID", value: user?.user_id || "—", span: "fill", nowrap: true },
    ],
    summaryDensity: "compact",
    title: `Envoyer un lien de réinitialisation à ${targetLabel} ?`,
    tone: "accent",
  };
}

function buildAccessUserCreationConfirmationModel({ email, fullName, password, roleKey, roleLabel }) {
  return {
    payload: {
      email,
      fullName,
      password,
      roleKey,
      roleLabel,
    },
    confirmLabel: "Créer et attribuer",
    description: "Le compte sera créé dans l’annuaire Supabase partagé avec Railway. Le rôle RBAC sera attribué uniquement dans l’environnement courant.",
    impactItems: [
      fullName ? `Nom initial: ${fullName}.` : null,
      `Rôle initial local à cet environnement: ${roleLabel}.`,
      "L’identité de connexion sera visible dans les environnements qui utilisent ce même projet Supabase.",
    ].filter(Boolean),
    summaryDensity: "compact",
    summaryItems: [
      { label: "Email", value: email },
      { label: "Nom", value: fullName || "—" },
      { label: "Rôle", value: roleLabel },
    ],
    title: `Créer le compte ${email} ?`,
    tone: "accent",
  };
}

function buildAccessUserRoleAssignConfirmationModel({
  currentRoleLabel = "",
  email = "",
  kind,
  nextRoleKey,
  nextRoleLabel,
  targetLabel,
  user = null,
  userId = "",
}) {
  const resolvedTargetLabel = targetLabel || user?.email || user?.user_id || "cet utilisateur";
  const resolvedCurrentRoleLabel = currentRoleLabel
    || user?.active_role_label
    || user?.active_role
    || "";

  return {
    kind,
    user,
    userId,
    email,
    nextRoleKey,
    nextRoleLabel,
    confirmLabel: "Appliquer",
    description: `Attribuer le rôle ${nextRoleLabel} à ${resolvedTargetLabel} ?`,
    impactItems: [
      "Le rôle actif sera mis à jour immédiatement côté backend.",
      "Les accès applicatifs de l’utilisateur seront rechargés après validation.",
    ],
    summaryDensity: "compact",
    summaryItems: [
      { label: "Utilisateur", value: resolvedTargetLabel, span: "hug" },
      resolvedCurrentRoleLabel
        ? { label: "Rôle actuel", value: resolvedCurrentRoleLabel, span: "hug" }
        : null,
      { label: "Nouveau rôle", value: nextRoleLabel, span: "hug" },
    ].filter(Boolean),
    title: `Attribuer le rôle ${nextRoleLabel} ?`,
    tone: "accent",
  };
}

function normalizeAccessUserEmail(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function isProtectedAccessAdminUser(user) {
  return normalizeAccessUserEmail(user?.email) === PROTECTED_ACCESS_ADMIN_EMAIL;
}

function isDriverPortalLinkedUser(user) {
  return Boolean(user?.user_id && user?.linked_truck_id && user?.account_type === "driver_portal");
}

function hasActiveDriverPortalRole(user) {
  return isDriverPortalLinkedUser(user)
    && user?.active_role === DRIVER_ROLE_KEY
    && user?.has_active_assignment === true;
}

const ACCESS_STATE_VALUES = new Set(["active", "suspended", "unassigned"]);

function resolveAccessUserState(user) {
  if (ACCESS_STATE_VALUES.has(user?.access_state)) {
    return user.access_state;
  }

  if (user?.status === "suspended" || user?.status === "access_suspended") {
    return "suspended";
  }

  // Legacy portal rows did not expose access_state. Keep their historical
  // role-based interpretation until the directory response is refreshed.
  if (isDriverPortalLinkedUser(user) && !hasActiveDriverPortalRole(user)) {
    return "suspended";
  }

  return user?.has_active_assignment === true ? "active" : "unassigned";
}

function hasSupabaseAccount(user) {
  return user?.has_supabase_account === true;
}

function isSupabaseAccountMissing(user) {
  return user?.status === "directory_unresolved" && !hasSupabaseAccount(user);
}

function resolveAccessUserRoleKey(user) {
  if (resolveAccessUserState(user) === "suspended" && user?.last_assigned_role) {
    return user.last_assigned_role;
  }

  return user?.active_role || user?.last_assigned_role || "";
}

function isAccessUserSuspended(user) {
  return resolveAccessUserState(user) === "suspended";
}

function resolveAccessUserStatusLabel(user) {
  if (isSupabaseAccountMissing(user)) {
    return "Compte Supabase absent";
  }
  const accessState = resolveAccessUserState(user);
  if (accessState === "suspended") {
    return "Suspendu";
  }
  if (accessState === "unassigned") {
    return "Non attribué";
  }
  return "Actif";
}

function resolveAccessUserStatusToneClassName(user) {
  if (isSupabaseAccountMissing(user)) {
    return pillToneClasses.danger;
  }
  const accessState = resolveAccessUserState(user);
  if (accessState === "suspended") {
    return pillToneClasses.warning;
  }
  if (accessState === "unassigned") {
    return pillToneClasses.outline;
  }
  return pillToneClasses.info;
}

const ACCESS_USER_DEFAULT_SORT_DIRECTIONS = {
  email: "asc",
  lastActivity: "desc",
  online: "desc",
  role: "asc",
  status: "asc",
};

const accessUserTextCollator = new Intl.Collator("fr-CA", {
  numeric: true,
  sensitivity: "base",
});

function resolveAccessUserEmailSortValue(user) {
  return normalizeAccessUserEmail(user?.email) || String(user?.user_id || "").trim().toLowerCase();
}

function resolveAccessUserLastActivityTimestamp(value) {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  }

  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function compareAccessUserLastActivity(leftUser, rightUser, { direction = "desc", nullsLast = true } = {}) {
  const leftTimestamp = resolveAccessUserLastActivityTimestamp(leftUser?.last_activity_at);
  const rightTimestamp = resolveAccessUserLastActivityTimestamp(rightUser?.last_activity_at);

  if (leftTimestamp == null || rightTimestamp == null) {
    if (leftTimestamp == null && rightTimestamp != null) {
      return nullsLast ? 1 : -1;
    }
    if (leftTimestamp != null && rightTimestamp == null) {
      return nullsLast ? -1 : 1;
    }
    return 0;
  }

  const comparison = leftTimestamp - rightTimestamp;
  return direction === "desc" ? -comparison : comparison;
}

function compareAccessUsers(leftUser, rightUser, sort) {
  const directionMultiplier = sort?.direction === "desc" ? -1 : 1;
  let comparison = 0;

  if (sort?.key === "online") {
    comparison = Number(Boolean(leftUser?.is_session_active)) - Number(Boolean(rightUser?.is_session_active));
  } else if (sort?.key === "lastActivity") {
    comparison = compareAccessUserLastActivity(leftUser, rightUser, {
      direction: sort?.direction === "asc" ? "asc" : "desc",
    });
    if (comparison !== 0) {
      return comparison;
    }
  } else if (sort?.key === "role") {
    comparison = accessUserTextCollator.compare(
      String(leftUser?.active_role_label || leftUser?.active_role || leftUser?.last_assigned_role || ""),
      String(rightUser?.active_role_label || rightUser?.active_role || rightUser?.last_assigned_role || ""),
    );
  } else if (sort?.key === "status") {
    comparison = accessUserTextCollator.compare(
      resolveAccessUserStatusLabel(leftUser),
      resolveAccessUserStatusLabel(rightUser),
    );
  } else {
    comparison = accessUserTextCollator.compare(
      resolveAccessUserEmailSortValue(leftUser),
      resolveAccessUserEmailSortValue(rightUser),
    );
  }

  if (comparison !== 0) {
    return comparison * directionMultiplier;
  }

  // Quand on trie par « En ligne », départager ensuite par dernière activité (plus récente en premier).
  if (sort?.key === "online") {
    const lastActivityComparison = compareAccessUserLastActivity(leftUser, rightUser, { direction: "desc" });
    if (lastActivityComparison !== 0) {
      return lastActivityComparison;
    }
  }

  return accessUserTextCollator.compare(
    resolveAccessUserEmailSortValue(leftUser),
    resolveAccessUserEmailSortValue(rightUser),
  );
}

function AccessUserSortableHeader({
  label,
  onSort,
  sort,
  sortKey,
}) {
  const direction = sort?.key === sortKey ? sort.direction : null;
  const indicator = direction === "asc" ? "↑" : direction === "desc" ? "↓" : "↕";
  const directionLabel = direction === "asc" ? "ordre croissant" : direction === "desc" ? "ordre décroissant" : "trier";

  return (
    <th aria-sort={direction === "asc" ? "ascending" : direction === "desc" ? "descending" : undefined} className="px-3 py-2.5 font-semibold">
      <button
        aria-label={`${label} — ${directionLabel}`}
        className="inline-flex items-center gap-1 font-semibold text-slate-600 transition hover:text-slate-950"
        onClick={() => onSort(sortKey)}
        type="button"
      >
        {label} <span aria-hidden="true">{indicator}</span>
      </button>
    </th>
  );
}

function canToggleAccessUser(user) {
  if (isProtectedAccessAdminUser(user)) {
    return false;
  }

  return hasSupabaseAccount(user);
}

function resolveDriverPortalAccessActionLabel(user) {
  return isAccessUserSuspended(user) ? "Réactiver" : "Suspendre";
}

function resolveAccessUserRemoveActionLabel(user) {
  return user?.linked_truck_id && isSupabaseAccountMissing(user) ? "Nettoyer" : "Supprimer";
}

function buildAccessUserRemoveConfirmationMessage(user) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";
  const truckLabel = user?.linked_truck_name || user?.linked_truck_id;
  if (user?.linked_truck_id && isSupabaseAccountMissing(user)) {
    return `Nettoyer le lien résiduel ${targetLabel} du camion ${truckLabel} ? Le compte Supabase Auth est déjà absent; l’état courant sera nettoyé immédiatement et l’autre environnement se réconciliera automatiquement.`;
  }
  // Lien camion hérité (trucks.driver_user_id) : même suppression que les autres comptes, le lien est effacé.
  if (user?.linked_truck_id) {
    return `Supprimer définitivement le compte ${targetLabel} et le délier du camion ${truckLabel} ? Le compte Supabase Auth et l’accès RBAC seront retirés de manière irréversible.`;
  }
  if (user?.account_type === "driver_portal") {
    return `Supprimer définitivement le compte chauffeur ${targetLabel} ? Le compte Supabase Auth, l’accès RBAC et l’entrée de l’annuaire seront retirés de manière irréversible.`;
  }

  return `Supprimer définitivement le compte ${targetLabel} ? Le compte Supabase Auth, l’accès RBAC et l’entrée de l’annuaire seront retirés de manière irréversible.`;
}

function buildAccessUserRemoveConfirmationModel(user, { sharedIdentityConfirmationRequired = false } = {}) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";
  const confirmationMessage = buildAccessUserRemoveConfirmationMessage(user);
  const hasLinkedTruck = Boolean(user?.linked_truck_id);
  const isMissingSupabaseAccount = isSupabaseAccountMissing(user);
  const isStaleLinkedAccount = hasLinkedTruck && isMissingSupabaseAccount;
  const isDriverPortalAccount = user?.account_type === "driver_portal";

  return {
    user,
    confirmCheckbox: sharedIdentityConfirmationRequired ? SHARED_IDENTITY_CONFIRMATION : null,
    confirmLabel: isStaleLinkedAccount ? "Nettoyer" : "Supprimer",
    description: confirmationMessage,
    impactItems: isStaleLinkedAccount
      ? [
        "Aucune identité Supabase ne sera recréée ou modifiée.",
        "Le lien camion et l’affectation RBAC résiduelle seront retirés ici; l’autre environnement fera le même nettoyage dès qu’il est actif.",
      ]
      : hasLinkedTruck
        ? [
          "Le compte Supabase Auth partagé sera supprimé dans tous les environnements.",
          "Les liens actifs seront retirés ici immédiatement, puis automatiquement dans l’autre environnement.",
        ]
        : [
          "Le compte Supabase Auth partagé sera supprimé définitivement dans tous les environnements.",
          "Les accès et liens actifs seront nettoyés ici immédiatement, puis automatiquement dans l’autre environnement.",
        ],
    requiresSharedIdentityConfirmation: sharedIdentityConfirmationRequired,
    summaryDensity: "compact",
    summaryItems: [
      { label: "Utilisateur", value: targetLabel },
      hasLinkedTruck
        ? { label: "Camion", value: user?.linked_truck_name || user?.linked_truck_id || "—" }
        : { label: "Type", value: "Compte Supabase Auth" },
      isStaleLinkedAccount
        ? { label: "Compte", value: "Supabase absent" }
        : isDriverPortalAccount
        ? { label: "Compte", value: "Chauffeur" }
        : null,
    ].filter(Boolean),
    title: confirmationMessage,
    tone: "danger",
    warningMessage: isStaleLinkedAccount
      ? "Cette action confirme l’absence du compte Supabase partagé; la réconciliation automatique nettoiera aussi l’autre environnement lorsqu’il est actif."
      : hasLinkedTruck
        ? "Cette suppression est définitive; le lien opérationnel sera retiré ici et automatiquement dans l’autre environnement."
        : "Cette suppression est définitive; l’autre environnement sera nettoyé automatiquement dès qu’il est actif.",
  };
}

function buildAccessUserSuspendConfirmationModel(
  user,
  { sharedIdentityConfirmationRequired = false, roleKey = "", roleLabel = "" } = {},
) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";
  const isDriverPortalUser = isDriverPortalLinkedUser(user);
  const preservesActiveRole = user?.has_active_assignment === true;
  const truckLabel = user?.linked_truck_name || user?.linked_truck_id;
  const isSuspended = isAccessUserSuspended(user);
  const resolvedRoleKey = isDriverPortalUser ? DRIVER_ROLE_KEY : roleKey;
  const resolvedRoleLabel = isDriverPortalUser
    ? roleLabel || "Chauffeur"
    : roleLabel || roleKey || user?.last_assigned_role || user?.active_role_label || user?.active_role || "le rôle sélectionné";

  if (isSuspended) {
    return {
      action: "reactivate",
      user,
      roleKey: resolvedRoleKey,
      confirmLabel: "Réactiver",
      description: preservesActiveRole
        ? `Réactiver le compte ${targetLabel} ? Le ban Supabase partagé sera levé et le rôle ${resolvedRoleLabel} restera inchangé dans l’environnement courant.`
        : `Réactiver le compte ${targetLabel} ? Le ban Supabase partagé sera levé, tandis que le rôle ${resolvedRoleLabel} sera restauré uniquement dans l’environnement courant.`,
      impactItems: [
        "Le ban Supabase sera levé pour tous les environnements qui utilisent ce même annuaire.",
        preservesActiveRole
          ? `Le rôle ${resolvedRoleLabel} déjà actif dans cet environnement sera conservé.`
          : `Le rôle ${resolvedRoleLabel} sera restauré seulement dans l’environnement courant.`,
      ],
      summaryDensity: "compact",
      summaryItems: [
        { label: "Utilisateur", value: targetLabel },
        isDriverPortalUser ? { label: "Camion", value: truckLabel || "—" } : null,
        { label: "Rôle restauré", value: resolvedRoleLabel },
      ].filter(Boolean),
      title: `Réactiver le compte ${targetLabel} ?`,
      tone: "accent",
      warningMessage: isDriverPortalUser
        ? "Le compte restera lié au camion et retrouvera l’accès au portail chauffeur."
        : "La connexion Supabase et l’accès SaaS seront rétablis avec le rôle sélectionné.",
    };
  }

  if (isDriverPortalUser) {
    return {
      action: "suspend",
      user,
      confirmCheckbox: sharedIdentityConfirmationRequired ? SHARED_IDENTITY_CONFIRMATION : null,
      confirmLabel: "Suspendre",
      description: `Suspendre le compte chauffeur ${targetLabel} ? La connexion Supabase et l’accès SaaS seront bloqués, mais le compte sera conservé et réactivable.`,
      impactItems: [
        "La connexion Supabase et l’accès au portail seront bloqués immédiatement.",
        "Le compte reste lié au camion et pourra être réactivé plus tard.",
      ],
      summaryDensity: "compact",
      requiresSharedIdentityConfirmation: sharedIdentityConfirmationRequired,
      summaryItems: [
        { label: "Utilisateur", value: targetLabel },
        { label: "Camion", value: truckLabel || "—" },
        { label: "Compte", value: "Chauffeur" },
      ],
      title: `Suspendre le compte chauffeur ${targetLabel} ?`,
      tone: "warning",
      warningMessage: "L’utilisateur perdra l’accès au portail chauffeur jusqu’à une réactivation manuelle.",
    };
  }

  return {
    action: "suspend",
    user,
    confirmCheckbox: sharedIdentityConfirmationRequired ? SHARED_IDENTITY_CONFIRMATION : null,
    confirmLabel: "Suspendre",
    description: `Suspendre le compte ${targetLabel} ? La connexion Supabase et l’accès SaaS seront bloqués, mais le compte sera conservé et réactivable.`,
    impactItems: [
      "La connexion Supabase et l’accès SaaS seront bloqués immédiatement.",
      "Le compte Supabase reste conservé et pourra être réactivé plus tard.",
    ],
    summaryDensity: "compact",
    requiresSharedIdentityConfirmation: sharedIdentityConfirmationRequired,
    summaryItems: [
      { label: "Utilisateur", value: targetLabel },
      { label: "Rôle actuel", value: user?.active_role_label || user?.active_role || user?.last_assigned_role || "—" },
    ],
    title: `Suspendre le compte ${targetLabel} ?`,
    tone: "warning",
    warningMessage: "L’utilisateur perdra sa connexion Supabase et son accès applicatif jusqu’à une réactivation manuelle.",
  };
}

function resolveAccessUserRemoveSuccessMessage(user) {
  const targetLabel = user?.email || user?.user_id || "cet utilisateur";
  if (user?.linked_truck_id) {
    return `Compte ${targetLabel} supprimé et délié du camion ${user.linked_truck_name || user.linked_truck_id}.`;
  }

  return `Compte Supabase Auth partagé supprimé définitivement pour ${targetLabel}; l’autre environnement sera nettoyé automatiquement dès qu’il est actif.`;
}

function resolveAuditOutcomeBadgeClassName(outcome) {
  if (outcome === "success") {
    return pillToneClasses.success;
  }
  if (outcome === "denied") {
    return pillToneClasses.danger;
  }
  if (outcome === "error") {
    return pillToneClasses.warning;
  }
  return pillToneClasses.outline;
}

function resolveAuditOutcomeLabel(outcome) {
  if (outcome === "success") {
    return "Succès";
  }
  if (outcome === "denied") {
    return "Refusé";
  }
  if (outcome === "error") {
    return "Erreur";
  }
  return "Inconnu";
}

function resolveAccessAuditActorLabel(entry) {
  return entry?.actor_email_snapshot || entry?.actor_user_id || "Acteur indisponible";
}

function resolveAccessAuditTargetLabel(entry) {
  return entry?.target_email_snapshot || entry?.target_user_id || "Cible indisponible";
}

function buildAccessAuditRoleTransition(entry) {
  const beforeRole = typeof entry?.before_json?.active_role === "string" ? entry.before_json.active_role : "";
  const afterRole = typeof entry?.after_json?.active_role === "string" ? entry.after_json.active_role : "";

  if (beforeRole && afterRole && beforeRole !== afterRole) {
    return `${beforeRole} -> ${afterRole}`;
  }

  if (!beforeRole && afterRole) {
    return `Rôle actif: ${afterRole}`;
  }

  return "";
}

function AccessTabButton({ active = false, icon: Icon, label, onClick }) {
  return (
    <button
      className={joinClasses([
        "inline-flex items-center gap-1.5 rounded-[4px] border px-2.5 py-1 text-[0.74rem] font-semibold transition",
        active
          ? "border-slate-900 bg-slate-900 text-white shadow-[0_8px_18px_rgba(15,23,42,0.16)]"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-950",
      ])}
      onClick={onClick}
      type="button"
    >
      <Icon aria-hidden="true" size={13} />
      {label}
    </button>
  );
}

function AccessRoleBadge({ compact = false, label, roleKey }) {
  return (
    <span className={joinClasses([
      compact
        ? ACCESS_ROLE_HEADER_PILL_CLASS_NAME
        : "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
      resolveRoleBadgeClassName(roleKey),
    ])}>
      {label}
    </span>
  );
}

function AccessRoleSelectionButton({ isSelected = false, onSelect, role }) {
  return (
    <button
      className={joinClasses([
        "flex min-w-max flex-1 flex-nowrap items-center justify-between gap-2 whitespace-nowrap rounded-[4px] border px-2 py-1.5 text-left transition",
        isSelected
          ? "border-slate-900 bg-slate-900 text-white shadow-[0_8px_18px_rgba(15,23,42,0.16)]"
          : "border-slate-200 bg-slate-50/75 text-slate-700 hover:border-slate-300 hover:bg-white",
      ])}
      onClick={() => onSelect(role.role_key)}
      type="button"
    >
      <div className="flex shrink-0 items-center gap-1.5">
        <p className="m-0 truncate text-[0.78rem] font-semibold tracking-[-0.02em]">{role.label}</p>
        {!role.is_active ? (
          <span className={joinClasses([
            "inline-flex shrink-0 items-center rounded-[4px] px-1.5 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.1em]",
            isSelected ? "bg-white/12 text-white" : pillToneClasses.warning,
          ])}>
            Archivé
          </span>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1 text-[0.64rem]">
        <span className={joinClasses([
          "inline-flex items-center rounded-[4px] px-1.5 py-0.5 font-bold uppercase tracking-[0.1em]",
          isSelected ? "bg-white/12 text-white" : pillToneClasses.outline,
        ])}>
          {role.permissions.length} perm.
        </span>
        <span className={joinClasses([
          "inline-flex items-center rounded-[4px] px-1.5 py-0.5 font-bold uppercase tracking-[0.1em]",
          isSelected ? "bg-white/12 text-white" : pillToneClasses.outline,
        ])}>
          {role.active_user_count} util.
        </span>
      </div>
    </button>
  );
}

function AccessUsersPanel({
  createUserState,
  currentUserId,
  draftRolesByUserId,
  isCreatingUser,
  isManualSubmitting,
  isRemovingUserId,
  isSavingUserId,
  isSendingResetEmailUserId,
  isTogglingDriverPortalUserId,
  isUpdatingPasswordUserId,
  manualAssignment,
  onApplyRoleChange,
  onCreateUserChange,
  onCreateUserSubmit,
  onDraftRoleChange,
  onManualAssignmentChange,
  onManualAssignmentSubmit,
  onOpenPasswordChange,
  onRemoveRole,
  onSendPasswordResetEmail,
  onToggleDriverPortalAccess,
  roles,
  usersState,
}) {
  const users = Array.isArray(usersState?.users) ? usersState.users : [];
  const [usersSort, setUsersSort] = React.useState({ key: null, direction: "asc" });
  const roleOptions = Array.isArray(roles) ? roles.filter((role) => role?.is_active !== false) : [];
  const isDirectoryAuthoritative = usersState?.directory?.source === "supabase_admin";
  const showLastActivityColumn = users.some((user) => Boolean(user?.last_activity_at));
  const hasCreateTarget = Boolean(createUserState.email.trim() && createUserState.password.trim() && createUserState.roleKey);
  const hasManualTarget = Boolean(manualAssignment.userId.trim() || manualAssignment.email.trim());
  const sortedUsers = React.useMemo(() => {
    if (!usersSort.key) {
      return users;
    }

    return [...users].sort((leftUser, rightUser) => compareAccessUsers(leftUser, rightUser, usersSort));
  }, [users, usersSort]);
  const handleUsersSort = React.useCallback((nextKey) => {
    setUsersSort((currentSort) => {
      if (currentSort.key === nextKey) {
        return {
          key: nextKey,
          direction: currentSort.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key: nextKey,
        direction: ACCESS_USER_DEFAULT_SORT_DIRECTIONS[nextKey] || "asc",
      };
    });
  }, []);

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 rounded-[4px] border border-black/6 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <div className="grid gap-0.5">
          <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Création SaaS</p>
          <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Créez un compte standard directement depuis le SaaS, avec email confirmé côté backend puis rôle RBAC appliqué immédiatement. Plus besoin d’ouvrir Supabase pour récupérer un UID.</p>
        </div>

        <div className="grid gap-2 min-[1180px]:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)_minmax(0,0.95fr)_minmax(180px,0.72fr)_auto] min-[1180px]:items-end">
          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Nom complet (optionnel)</span>
            <input
              autoComplete="name"
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isCreatingUser}
              onChange={(event) => onCreateUserChange("fullName", event.target.value)}
              placeholder="Jean Tremblay"
              type="text"
              value={createUserState.fullName}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Email de connexion</span>
            <input
              autoComplete="email"
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isCreatingUser}
              onChange={(event) => onCreateUserChange("email", event.target.value)}
              placeholder="utilisateur@example.com"
              type="email"
              value={createUserState.email}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Mot de passe initial</span>
            <input
              autoComplete="new-password"
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isCreatingUser}
              onChange={(event) => onCreateUserChange("password", event.target.value)}
              placeholder="Minimum 8 caractères"
              type="password"
              value={createUserState.password}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Rôle initial</span>
            <select
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isCreatingUser || roleOptions.length === 0}
              onChange={(event) => onCreateUserChange("roleKey", event.target.value)}
              value={createUserState.roleKey}
            >
              {roleOptions.map((role) => (
                <option key={role.role_key} value={role.role_key}>{role.label}</option>
              ))}
            </select>
          </label>

          <button
            className={PRIMARY_ACTION_CLASS_NAME}
            disabled={!hasCreateTarget || isCreatingUser || roleOptions.length === 0}
            onClick={onCreateUserSubmit}
            type="button"
          >
            {isCreatingUser ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
            Créer et attribuer
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-[4px] border border-black/6 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <div className="grid gap-0.5">
          <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Affectation manuelle</p>
          <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Attribuez un rôle à un compte déjà existant. L’UID Supabase reste accepté, mais l’email peut suffire si l’UID est vide et que l’annuaire admin Supabase est disponible côté backend. Seuls les rôles actifs sont proposés à l’affectation.</p>
        </div>

        <div className="grid gap-2 min-[980px]:grid-cols-[minmax(0,1.35fr)_minmax(0,1.1fr)_minmax(180px,0.7fr)_auto] min-[980px]:items-end">
          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">UID Supabase</span>
            <input
              autoComplete="off"
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isManualSubmitting}
              onChange={(event) => onManualAssignmentChange("userId", event.target.value)}
              placeholder="7c1a3d8e-..."
              type="text"
              value={manualAssignment.userId}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Email (optionnel)</span>
            <input
              autoComplete="off"
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isManualSubmitting}
              onChange={(event) => onManualAssignmentChange("email", event.target.value)}
              placeholder="utilisateur@example.com"
              type="email"
              value={manualAssignment.email}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Rôle</span>
            <select
              className={ACCESS_FORM_CONTROL_CLASS_NAME}
              disabled={isManualSubmitting || roleOptions.length === 0}
              onChange={(event) => onManualAssignmentChange("roleKey", event.target.value)}
              value={manualAssignment.roleKey}
            >
              {roleOptions.map((role) => (
                <option key={role.role_key} value={role.role_key}>{role.label}</option>
              ))}
            </select>
          </label>

          <button
            className={PRIMARY_ACTION_CLASS_NAME}
            disabled={!hasManualTarget || !manualAssignment.roleKey || isManualSubmitting || roleOptions.length === 0}
            onClick={onManualAssignmentSubmit}
            type="button"
          >
            {isManualSubmitting ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
            Appliquer
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-2.5">
        <div className="grid gap-0.5">
          <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Affectations de rôle</p>
          <p className="m-0 text-[0.76rem] leading-5 text-slate-600">La modification est appliquée côté backend puis la liste est rechargée. Cette table fusionne l’annuaire disponible et les affectations RBAC actives pour garder une entrée exploitable même si l’annuaire est partiel.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
            pillToneClasses.outline,
          ])}>
            {users.length} utilisateur(s)
          </span>
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
            usersState?.directory?.source === "supabase_admin" ? pillToneClasses.info : pillToneClasses.outline,
          ])}>
            {usersState?.directory?.source === "supabase_admin" ? "Annuaire + RBAC" : "Affectations RBAC"}
          </span>
        </div>
      </div>

      {users.length ? (
        <div className="overflow-hidden rounded-[4px] border border-slate-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-[0.82rem] text-slate-700">
              <thead className="bg-slate-50 text-left text-[0.68rem] uppercase tracking-[0.12em] text-slate-500">
                <tr>
                  <AccessUserSortableHeader label="Email" onSort={handleUsersSort} sort={usersSort} sortKey="email" />
                  <AccessUserSortableHeader label="Rôle actuel" onSort={handleUsersSort} sort={usersSort} sortKey="role" />
                  <AccessUserSortableHeader label="Statut" onSort={handleUsersSort} sort={usersSort} sortKey="status" />
                  <AccessUserSortableHeader label="En ligne" onSort={handleUsersSort} sort={usersSort} sortKey="online" />
                  {showLastActivityColumn ? <AccessUserSortableHeader label="Dernière activité" onSort={handleUsersSort} sort={usersSort} sortKey="lastActivity" /> : null}
                  <th className="px-3 py-2.5 font-semibold">Changer le rôle</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => {
                  const currentRoleKey = resolveAccessUserRoleKey(user);
                  const nextRole = resolveAccessRoleKey(draftRolesByUserId[user.user_id] || currentRoleKey, roleOptions);
                  const isDirty = nextRole !== currentRoleKey;
                  const isSavingRow = isSavingUserId === user.user_id;
                  const isRemovingRow = isRemovingUserId === user.user_id;
                  const isTogglingDriverPortalRow = isTogglingDriverPortalUserId === user.user_id;
                  const isUpdatingPasswordRow = isUpdatingPasswordUserId === user.user_id;
                  const isSendingResetEmailRow = isSendingResetEmailUserId === user.user_id;
                  const isCredentialMutationRow = isUpdatingPasswordRow || isSendingResetEmailRow || isTogglingDriverPortalRow;
                  const isProtectedAdminUser = isProtectedAccessAdminUser(user);
                  const canRemoveUser = !isProtectedAdminUser && Boolean(user.linked_truck_id || hasSupabaseAccount(user));
                  const canToggleUserAccess = canToggleAccessUser(user);
                  const driverPortalActionLabel = resolveDriverPortalAccessActionLabel(user);
                  const isSuspendedUser = isAccessUserSuspended(user);
                  const isMissingSupabaseAccount = isSupabaseAccountMissing(user);
                  const canSendPasswordResetEmail = isDirectoryAuthoritative && hasSupabaseAccount(user) && Boolean(user.email);
                  const preservesActiveRole = isSuspendedUser && user?.has_active_assignment === true;
                  // Pas pour soi-même ni pour les chauffeurs : le portail n'a pas ces réglages.
                  const canSetUserPreferences = user.user_id !== currentUserId && user.has_active_assignment === true
                    && !isSuspendedUser && user.active_role !== DRIVER_ROLE_KEY;

                  return (
                    <tr key={user.user_id} className="border-t border-slate-200 align-top">
                      <td className="px-3 py-3">
                        <div className="grid gap-0.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="m-0 font-semibold tracking-[-0.02em] text-slate-950">{user.email || "Email indisponible"}</p>
                            {user.user_id === currentUserId ? (
                              <span className={joinClasses([
                                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                                pillToneClasses.info,
                              ])}>
                                Vous
                              </span>
                            ) : null}
                          </div>
                          {user.full_name ? (
                            <p className="m-0 text-[0.76rem] font-medium leading-5 text-slate-700">{user.full_name}</p>
                          ) : null}
                          <p className="m-0 text-[0.72rem] leading-5 text-slate-500">{user.user_id}</p>
                          {canSetUserPreferences ? (
                            <button
                              className={joinClasses([ACCESS_TABLE_SECONDARY_ACTION_CLASS_NAME, "mt-1 justify-self-start gap-1.5"])}
                              onClick={() => startPreferencesTarget({
                                userId: user.user_id,
                                email: user.email || user.full_name || "",
                                roleLabel: user.active_role_label || user.active_role || "",
                              })}
                              title="Ouvre ses préférences sur toutes les pages : affichage, colonnes, volets, disposition et filtres."
                              type="button"
                            >
                              <SlidersHorizontal aria-hidden="true" size={13} />
                              Régler ses préférences
                            </button>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="grid gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            {!isProtectedAdminUser ? (
                              <AccessRoleBadge
                                label={user.active_role_label || user.active_role || user.last_assigned_role || "—"}
                                roleKey={user.active_role || user.last_assigned_role}
                              />
                            ) : null}
                            {isProtectedAdminUser ? (
                              <span className={joinClasses([
                                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                                pillToneClasses.warning,
                              ])}>
                                Admin principal
                              </span>
                            ) : null}
                          </div>
                          <p className="m-0 text-[0.72rem] uppercase tracking-[0.12em] text-slate-400">{user.active_role || user.last_assigned_role || "—"}</p>
                          {user.status === "uid_mismatch" ? (
                            <p className="m-0 text-[0.74rem] leading-5 text-amber-700">Affectation liée à un ancien UID Supabase: réappliquez le rôle pour rattacher le compte actif.</p>
                          ) : null}
                          {isMissingSupabaseAccount ? (
                            <p className="m-0 text-[0.74rem] leading-5 text-rose-700">L’affectation RBAC locale est résiduelle, mais aucun compte Supabase n’existe pour ouvrir une session.</p>
                          ) : resolveAccessUserState(user) === "suspended" ? (
                            <p className="m-0 text-[0.74rem] leading-5 text-amber-700">Aucun rôle RBAC actif: la réactivation restaurera le rôle sélectionné.</p>
                          ) : !user.has_active_assignment ? (
                            <p className="m-0 text-[0.74rem] leading-5 text-amber-700">Aucun rôle RBAC actif: ce rôle est actuellement un fallback applicatif.</p>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={joinClasses([
                          "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                          resolveAccessUserStatusToneClassName(user),
                        ])}>
                          {resolveAccessUserStatusLabel(user)}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={joinClasses([
                            "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                            user.is_session_active ? pillToneClasses.success : pillToneClasses.outline,
                          ])}
                          title={user.session_last_seen_at ? `Présence confirmée: ${formatDateTime(user.session_last_seen_at)}` : "Aucune présence récente confirmée"}
                        >
                          {user.is_session_active ? "Oui" : "Non"}
                        </span>
                        {user.is_session_active && user.session_section ? (
                          <p className="m-0 mt-1 whitespace-nowrap text-[0.74rem] text-slate-600">{user.session_section}</p>
                        ) : null}
                      </td>
                      {showLastActivityColumn ? (
                        <td className="px-3 py-3 text-[0.78rem] text-slate-600">{formatDateTime(user.last_activity_at)}</td>
                      ) : null}
                      <td className="px-3 py-3">
                        <div className="grid gap-2">
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <select
                              className={ACCESS_TABLE_ROLE_SELECT_CLASS_NAME}
                              disabled={isProtectedAdminUser || isMissingSupabaseAccount || preservesActiveRole || isSavingRow || isRemovingRow || isCredentialMutationRow || roleOptions.length === 0}
                              onChange={(event) => onDraftRoleChange(user.user_id, event.target.value)}
                              title={isProtectedAdminUser
                                ? "Le compte admin principal doit rester Admin"
                                : isMissingSupabaseAccount
                                  ? "Aucun compte Supabase n’existe pour cette affectation RBAC"
                                  : preservesActiveRole
                                    ? "Le rôle local actif sera conservé pendant la réactivation Supabase"
                                  : "Changer le rôle"}
                              value={nextRole}
                            >
                              {roleOptions.map((role) => (
                                <option key={role.role_key} value={role.role_key}>{role.label}</option>
                              ))}
                            </select>
                            <button
                              className={ACCESS_TABLE_PRIMARY_ACTION_CLASS_NAME}
                              disabled={isProtectedAdminUser || isMissingSupabaseAccount || isSuspendedUser || !isDirty || isSavingRow || isRemovingRow || isCredentialMutationRow || roleOptions.length === 0}
                              onClick={() => onApplyRoleChange(user)}
                              title={isProtectedAdminUser
                                ? "Le compte admin principal doit rester Admin"
                                : isMissingSupabaseAccount
                                  ? "Le compte Supabase est absent; le rôle local ne peut pas être modifié ici"
                                : isSuspendedUser
                                  ? "Choisissez le rôle puis utilisez Réactiver"
                                  : "Appliquer le rôle"}
                              type="button"
                            >
                              {isSavingRow ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                              Appliquer
                            </button>
                            <button
                              aria-label={`${driverPortalActionLabel} le compte`}
                              className={joinClasses([
                                isSuspendedUser
                                  ? ACCESS_TABLE_REACTIVATE_ACTION_CLASS_NAME
                                  : ACCESS_TABLE_SUSPEND_ACTION_CLASS_NAME,
                                !canToggleUserAccess ? "opacity-60" : "",
                              ])}
                              disabled={!canToggleUserAccess || isSavingRow || isRemovingRow || isCredentialMutationRow}
                              onClick={() => onToggleDriverPortalAccess(user)}
                              title={driverPortalActionLabel}
                              type="button"
                            >
                              {isTogglingDriverPortalRow ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                              {isTogglingDriverPortalRow ? (isSuspendedUser ? "Réactivation..." : "Suspension...") : driverPortalActionLabel}
                            </button>
                            <button
                              className={joinClasses([
                                ACCESS_TABLE_DELETE_ACTION_CLASS_NAME,
                                !canRemoveUser ? "opacity-60" : "text-danger hover:border-danger/30 hover:bg-danger/5 hover:text-danger",
                              ])}
                              disabled={!canRemoveUser || isSavingRow || isRemovingRow || isCredentialMutationRow}
                              onClick={() => onRemoveRole(user)}
                              title={isProtectedAdminUser ? "Le compte admin principal ne peut pas être supprimé" : resolveAccessUserRemoveActionLabel(user)}
                              type="button"
                            >
                              {isRemovingRow ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                              {resolveAccessUserRemoveActionLabel(user)}
                            </button>
                            <button
                              className={joinClasses([
                                ACCESS_TABLE_SECONDARY_ACTION_CLASS_NAME,
                                "border-accent/18 text-accent hover:border-accent/35 hover:bg-accent/5 hover:text-accent-strong",
                              ])}
                              disabled={isMissingSupabaseAccount || isSavingRow || isRemovingRow || isCredentialMutationRow}
                              onClick={() => onOpenPasswordChange(user)}
                              title="Définir un mot de passe"
                              type="button"
                            >
                              {isUpdatingPasswordRow ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                              Modif.
                            </button>
                            <button
                              className={ACCESS_TABLE_SECONDARY_ACTION_CLASS_NAME}
                              disabled={!canSendPasswordResetEmail || isSavingRow || isRemovingRow || isCredentialMutationRow}
                              onClick={() => onSendPasswordResetEmail(user)}
                              title="Envoyer le lien de réinitialisation"
                              type="button"
                            >
                              {isSendingResetEmailRow ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                              Lien
                            </button>
                          </div>

                          <p className="m-0 text-[0.72rem] leading-5 text-slate-500">
                            Définissez un mot de passe immédiatement ou envoyez un lien de réinitialisation autonome vers /reset-password.
                          </p>
                          {isProtectedAdminUser ? (
                            <p className="m-0 text-[0.72rem] leading-5 text-amber-700">
                              Ce compte reste protégé pour conserver l’accès Admin principal.
                            </p>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-[0.84rem] leading-6 text-slate-600">
          Aucun utilisateur exploitable n’a encore été trouvé dans la liste. Les panneaux de création et d’affectation manuelle restent disponibles pour provisionner un compte puis lui appliquer un rôle.
        </div>
      )}
    </div>
  );
}

function AccessRolesPanel({
  createRoleState,
  isArchivingRole,
  isCreatingRole,
  isSavingRole,
  onArchiveRole,
  onCreateRoleCancel,
  onCreateRoleChange,
  onCreateRoleSubmit,
  onOpenCreateRole,
  onRoleDraftChange,
  onRoleDraftReset,
  onRolePermissionSearchChange,
  onRolePermissionToggle,
  onSaveRole,
  onSelectRole,
  permissionGroups,
  roleDraft,
  rolePermissionSearch,
  roles,
  selectedRoleKey,
}) {
  const [isEditingRoleDetails, setIsEditingRoleDetails] = React.useState(false);
  React.useEffect(() => setIsEditingRoleDetails(false), [selectedRoleKey]);
  const systemRoles = React.useMemo(() => SYSTEM_ROLE_DISPLAY_ORDER
    .map((roleKey) => roles.find((role) => role.role_key === roleKey))
    .filter((role) => role && typeof role === "object"), [roles]);
  const customRoles = React.useMemo(
    () => roles.filter((role) => !SYSTEM_ROLE_KEY_SET.has(role?.role_key)),
    [roles],
  );
  const selectedRole = roles.find((role) => role.role_key === selectedRoleKey)
    || systemRoles[0]
    || customRoles[0]
    || roles[0]
    || null;
  const normalizedPermissionSearch = rolePermissionSearch.trim().toLowerCase();
  const filteredPermissionGroups = React.useMemo(() => buildRoleEditorPermissionGroups(
    Array.isArray(permissionGroups)
      ? permissionGroups
          .map((group) => ({
            ...group,
            permissions: Array.isArray(group.permissions)
              ? group.permissions.filter((permission) => resolvePermissionSearchMatch(permission, normalizedPermissionSearch))
              : [],
          }))
          .filter((group) => group.permissions.length > 0)
      : [],
  ), [normalizedPermissionSearch, permissionGroups]);
  const filteredPermissionFamilies = React.useMemo(
    () => buildPermissionFamilies(filteredPermissionGroups),
    [filteredPermissionGroups],
  );
  if (!roles.length) {
    return (
      <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-[0.84rem] leading-6 text-slate-600">
        Aucun rôle RBAC n’a encore été remonté par le backend.
      </div>
    );
  }

  const selectedPermissionKeySet = new Set(roleDraft?.permissionKeys || []);
  const isArchived = selectedRole?.is_active === false;
  const isDirty = isRoleDraftDirty(selectedRole, roleDraft);

  return (
    <div className="access-roles-panel grid gap-2">
      <section className="grid gap-2 rounded-[4px] border border-black/6 bg-white px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="m-0 text-[0.8rem] font-semibold tracking-[-0.02em] text-slate-950">Rôles RBAC</p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Gérer les rôles"
              className={joinClasses([ACCESS_FORM_CONTROL_CLASS_NAME, "py-1.5 text-[0.78rem]"])}
              style={{ width: "16rem", maxWidth: "100%" }}
              disabled={isSavingRole || isCreatingRole || isArchivingRole}
              value=""
              onChange={(event) => {
                if (event.target.value === "create") {
                  setIsEditingRoleDetails(false);
                  onOpenCreateRole();
                } else if (event.target.value === "edit") {
                  onCreateRoleCancel();
                  setIsEditingRoleDetails((isOpen) => !isOpen);
                }
              }}
            >
              <option value="" disabled>Gérer les rôles</option>
              <option value="create">Créer un rôle par clonage</option>
              <option value="edit" disabled={isArchived}>
                {isEditingRoleDetails ? "Masquer le nom et la description" : "Modifier le nom et la description"}
              </option>
            </select>
          </div>
        </div>

        {createRoleState ? (
          <div className="grid gap-2.5 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-3">
            <div className="grid gap-0.5">
              <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">Nouveau rôle custom par clonage</p>
              <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Le nouveau rôle récupère la matrice du rôle source, puis devient éditable indépendamment.</p>
            </div>

            <label className="grid gap-1">
              <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Rôle source</span>
              <select
                className={ACCESS_FORM_CONTROL_CLASS_NAME}
                disabled={isCreatingRole}
                onChange={(event) => onCreateRoleChange("baseRoleKey", event.target.value)}
                value={createRoleState.baseRoleKey}
              >
                {roles.filter((role) => role.is_active).map((role) => (
                  <option key={role.role_key} value={role.role_key}>{role.label}</option>
                ))}
              </select>
            </label>

            <label className="grid gap-1">
              <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Libellé</span>
              <input
                className={ACCESS_FORM_CONTROL_CLASS_NAME}
                disabled={isCreatingRole}
                onChange={(event) => onCreateRoleChange("label", event.target.value)}
                placeholder="Ex. Responsable accès terrain"
                type="text"
                value={createRoleState.label}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Clé technique</span>
              <input
                className={ACCESS_FORM_CONTROL_CLASS_NAME}
                disabled={isCreatingRole}
                onChange={(event) => onCreateRoleChange("roleKey", event.target.value)}
                placeholder="responsable_acces_terrain"
                type="text"
                value={createRoleState.roleKey}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Description</span>
              <textarea
                className={joinClasses([ACCESS_FORM_CONTROL_CLASS_NAME, "min-h-22 resize-y"])}
                disabled={isCreatingRole}
                onChange={(event) => onCreateRoleChange("description", event.target.value)}
                placeholder="Description fonctionnelle du rôle"
                value={createRoleState.description}
              />
            </label>

            <div className="flex flex-wrap justify-end gap-2">
              <button className={SECONDARY_ACTION_CLASS_NAME} disabled={isCreatingRole} onClick={onCreateRoleCancel} type="button">
                Annuler
              </button>
              <button className={PRIMARY_ACTION_CLASS_NAME} disabled={isCreatingRole} onClick={onCreateRoleSubmit} type="button">
                {isCreatingRole ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                Créer le rôle
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-nowrap gap-1 overflow-x-auto">
          {[...systemRoles, ...customRoles].map((role) => (
            <AccessRoleSelectionButton
              isSelected={role.role_key === selectedRole?.role_key}
              key={role.role_key}
              onSelect={onSelectRole}
              role={role}
            />
          ))}
        </div>

      </section>

      <section className="access-role-editor flex min-w-0 flex-col gap-2 rounded-[4px] border border-black/6 bg-white px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
        {selectedRole && roleDraft ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="relative flex max-w-80 items-center">
                <span className="sr-only">Recherche permission</span>
                <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 text-slate-400" size={13} />
                <input
                  className={joinClasses([ACCESS_FORM_CONTROL_CLASS_NAME, "py-1.5 pl-8 text-[0.78rem]"])}
                  disabled={isSavingRole}
                  onChange={(event) => onRolePermissionSearchChange(event.target.value)}
                  placeholder="Rechercher une permission"
                  type="search"
                  value={rolePermissionSearch}
                />
              </label>

              <div className="flex flex-wrap items-center gap-1.5">
                <button className={SECONDARY_ACTION_CLASS_NAME} disabled={!isDirty || isSavingRole || isArchived} onClick={onRoleDraftReset} type="button">
                  Annuler
                </button>
                {!selectedRole.is_system && selectedRole.is_active ? (
                  <button
                    className={joinClasses([
                      SECONDARY_ACTION_CLASS_NAME,
                      "text-danger hover:border-danger/30 hover:bg-danger/5 hover:text-danger",
                    ])}
                    disabled={isSavingRole || isArchivingRole}
                    onClick={onArchiveRole}
                    type="button"
                  >
                    {isArchivingRole ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : <Archive aria-hidden="true" size={15} />}
                    Archiver
                  </button>
                ) : null}
                <button className={PRIMARY_ACTION_CLASS_NAME} disabled={!isDirty || isSavingRole || isArchived} onClick={onSaveRole} type="button">
                  {isSavingRole ? <LoaderCircle className="animate-spin" aria-hidden="true" size={15} /> : null}
                  Enregistrer
                </button>
              </div>
            </div>

            {isArchived ? (
              <div className={joinClasses(["rounded-[4px] px-3 py-2 text-[0.8rem] font-medium", resolveFeedbackToneClassName("info")])}>
                Ce rôle est archivé. Sa matrice reste visible en lecture seule pour référence et audit.
              </div>
            ) : null}

            {isEditingRoleDetails ? (
              <div className="grid gap-1.5 min-[980px]:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
                <label className="grid gap-0.5">
                  <span className="text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Libellé</span>
                  <input
                    className={joinClasses([ACCESS_FORM_CONTROL_CLASS_NAME, "py-1.5 text-[0.8rem]"])}
                    disabled={isSavingRole || isArchived}
                    onChange={(event) => onRoleDraftChange("label", event.target.value)}
                    type="text"
                    value={roleDraft.label}
                  />
                </label>
                <label className="grid gap-0.5">
                  <span className="text-[0.64rem] font-semibold uppercase tracking-[0.12em] text-slate-500">Description</span>
                  <textarea
                    className={joinClasses([ACCESS_FORM_CONTROL_CLASS_NAME, "min-h-11 resize-y py-1.5 text-[0.8rem]"])}
                    disabled={isSavingRole || isArchived}
                    onChange={(event) => onRoleDraftChange("description", event.target.value)}
                    rows={2}
                    value={roleDraft.description}
                  />
                </label>
              </div>
            ) : null}

            <PermissionFamilyJumpNav families={filteredPermissionFamilies} />

            {filteredPermissionGroups.length ? (
              <div className="access-role-permissions grid gap-2 min-[1180px]:max-h-[calc(100vh-15rem)] min-[1180px]:overflow-y-auto min-[1180px]:pr-1">
                {filteredPermissionFamilies.map((family) => (
                  <section id={`admin-permission-family-${family.key}`} key={family.key} className="scroll-mt-4 grid gap-1.5">
                    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200/90 pb-1.5">
                      <div className="grid gap-0.5 min-w-0">
                        <p className="m-0 text-[1.02rem] font-semibold tracking-[-0.03em] text-slate-950">{family.title}</p>
                        <p className="m-0 max-w-272 text-[0.78rem] leading-5 text-slate-600">{family.description}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={joinClasses([
                          "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                          pillToneClasses.outline,
                        ])}>
                          {family.groups.length} groupe(s)
                        </span>
                        <span className={joinClasses([
                          "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                          pillToneClasses.outline,
                        ])}>
                          {family.permissionCount} permission(s)
                        </span>
                      </div>
                    </div>

                    <div className={resolvePermissionGroupGridClassName(family.groups.length)}>
                      {family.groups.map((group) => {
                        const permissionGridClassName = resolvePermissionItemsGridClassName(group);
                        const visibleGroupPermissions = group.permissions;
                        const isRedundantGroup = family.groups.length === 1
                          && group.label?.trim().toLowerCase() === family.title?.trim().toLowerCase();

                        return (
                          <div key={group.scope} className={isRedundantGroup ? "grid min-w-0 gap-1.5" : resolvePermissionGroupCardClassName(group)}>
                            {!isRedundantGroup ? (
                              <p className="m-0 text-[0.9rem] font-semibold tracking-[-0.025em] text-slate-950">{group.label}</p>
                            ) : null}

                            {isDataManagerPermissionGroup(group) ? (
                              <DataManagerPermissionSections
                                permissions={visibleGroupPermissions}
                                renderPermission={(permission) => {
                                  const isChecked = selectedPermissionKeySet.has(permission.permission_key);

                                  return (
                                    <label key={permission.permission_key} className={joinClasses([
                                      "grid gap-1 rounded-[4px] border px-2.5 py-2 transition min-w-0",
                                      isChecked ? "border-slate-900 bg-white" : "border-slate-200 bg-white/75",
                                      isArchived ? "opacity-80" : "cursor-pointer hover:border-slate-300",
                                    ])}>
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <span className="flex min-w-0 items-start gap-2.5">
                                          <input
                                            checked={isChecked}
                                            className="mt-0.5 h-4 w-4 rounded-[4px] border-slate-300 text-slate-900"
                                            disabled={isSavingRole || isArchived}
                                            onChange={() => onRolePermissionToggle(permission.permission_key)}
                                            type="checkbox"
                                          />
                                          <span className="grid gap-0.5 min-w-0">
                                            <span className="text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">{permission.label}</span>
                                          </span>
                                        </span>
                                        <span className="flex flex-wrap items-center gap-1.5">
                                          {isSensitivePermission(permission) ? (
                                            <span className={joinClasses([
                                              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                                              pillToneClasses.warning,
                                            ])}>
                                              Sensible
                                            </span>
                                          ) : null}
                                        </span>
                                      </div>
                                      <p className="m-0 text-[0.74rem] leading-4.5 text-slate-600">{permission.description || "Permission catalogue sans description supplémentaire."}</p>
                                    </label>
                                  );
                                }}
                              />
                            ) : (
                              <div className={permissionGridClassName}>
                                {visibleGroupPermissions.map((permission) => {
                                  const isChecked = selectedPermissionKeySet.has(permission.permission_key);

                                  return (
                                    <label key={permission.permission_key} className={joinClasses([
                                      "grid gap-1 rounded-[4px] border px-2.5 py-2 transition min-w-0",
                                      isChecked ? "border-slate-900 bg-white" : "border-slate-200 bg-white/75",
                                      isArchived ? "opacity-80" : "cursor-pointer hover:border-slate-300",
                                    ])}>
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <span className="flex min-w-0 items-start gap-2.5">
                                          <input
                                            checked={isChecked}
                                            className="mt-0.5 h-4 w-4 rounded-[4px] border-slate-300 text-slate-900"
                                            disabled={isSavingRole || isArchived}
                                            onChange={() => onRolePermissionToggle(permission.permission_key)}
                                            type="checkbox"
                                          />
                                          <span className="grid gap-0.5 min-w-0">
                                            <span className="text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">{permission.label}</span>
                                          </span>
                                        </span>
                                        <span className="flex flex-wrap items-center gap-1.5">
                                          {isSensitivePermission(permission) ? (
                                            <span className={joinClasses([
                                              "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em]",
                                              pillToneClasses.warning,
                                            ])}>
                                              Sensible
                                            </span>
                                          ) : null}
                                        </span>
                                      </div>
                                      <p className="m-0 text-[0.74rem] leading-4.5 text-slate-600">{permission.description || "Permission catalogue sans description supplémentaire."}</p>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-[0.78rem] leading-5 text-slate-600">
                Aucune permission du catalogue ne correspond à la recherche en cours.
              </div>
            )}
          </>
        ) : (
          <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-[0.78rem] leading-5 text-slate-600">
            Sélectionnez un rôle pour afficher son détail et éditer sa matrice.
          </div>
        )}
      </section>
    </div>
  );
}

function AccessPermissionsPanel({ groups }) {
  const permissionFamilies = React.useMemo(() => buildPermissionFamilies(groups), [groups]);

  if (!permissionFamilies.length) {
    return (
      <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-[0.84rem] leading-6 text-slate-600">
        Le catalogue de permissions n’a pas encore été remonté par le backend.
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <PermissionFamilyJumpNav families={permissionFamilies} />
      {permissionFamilies.map((family) => (
        <section id={`admin-permission-family-${family.key}`} key={family.key} className="scroll-mt-4 grid gap-3 rounded-[4px] border border-slate-300 bg-slate-50/90 px-3.5 py-3 shadow-page-loading-meta">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200/90 pb-2.5">
            <div className="grid gap-0.5 min-w-0">
              <p className="m-0 text-[1.02rem] font-semibold tracking-[-0.03em] text-slate-950">{family.title}</p>
              <p className="m-0 max-w-272 text-[0.78rem] leading-5 text-slate-600">{family.description}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                pillToneClasses.outline,
              ])}>
                {family.groups.length} groupe(s)
              </span>
              <span className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                pillToneClasses.outline,
              ])}>
                {family.permissionCount} permission(s)
              </span>
            </div>
          </div>

          <div className={resolvePermissionGroupGridClassName(family.groups.length)}>
            {family.groups.map((group) => {
              const visibleGroupPermissions = group.permissions;

              return (
              <article key={group.scope} className={joinClasses([
                "grid gap-2.5 rounded-[4px] border border-slate-300 bg-white px-3.5 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]",
                ROLE_EDITOR_WIDE_LAYOUT_SCOPES.has(group.scope) ? "min-[1180px]:col-span-full" : "",
              ])}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="grid gap-0.75 min-w-0">
                    <p className="m-0 text-[0.92rem] font-semibold tracking-[-0.025em] text-slate-950">{group.label}</p>
                  </div>
                </div>

                {isDataManagerPermissionGroup(group) ? (
                  <DataManagerPermissionSections
                    permissions={visibleGroupPermissions}
                    renderPermission={(permission) => (
                      <div key={permission.permission_key} className="grid gap-0.75 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                        <div className="grid gap-0.5 min-w-0">
                          <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">{permission.label}</p>
                        </div>
                        <p className="m-0 text-[0.76rem] leading-5 text-slate-600">{permission.description || "Permission catalogue en lecture seule."}</p>
                      </div>
                    )}
                  />
                ) : (
                  <div className={resolvePermissionItemsGridClassName({ permissions: visibleGroupPermissions })}>
                    {visibleGroupPermissions.map((permission) => (
                      <div key={permission.permission_key} className="grid gap-0.75 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                        <div className="grid gap-0.5 min-w-0">
                          <p className="m-0 text-[0.82rem] font-semibold tracking-[-0.02em] text-slate-950">{permission.label}</p>
                        </div>
                        <p className="m-0 text-[0.76rem] leading-5 text-slate-600">{permission.description || "Permission catalogue en lecture seule."}</p>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function AccessAuditPanel({ auditError, auditState, isLoading }) {
  const items = Array.isArray(auditState?.items) ? auditState.items : [];

  if (isLoading && !items.length) {
    return (
      <div className="flex min-h-40 items-center justify-center rounded-[4px] border border-slate-200 bg-slate-50/70 px-4 py-6 text-slate-600">
        <div className="flex items-center gap-3">
          <LoaderCircle aria-hidden="true" className="animate-spin" size={18} />
          <span className="text-[0.92rem] font-medium">Chargement de l’audit d’accès…</span>
        </div>
      </div>
    );
  }

  if (auditError && !items.length) {
    return (
      <div className="grid gap-3 rounded-[4px] border border-border-danger-subtle bg-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        <div className="flex items-center gap-3 text-danger">
          <AlertTriangle aria-hidden="true" size={18} />
          <p className="m-0 text-[0.95rem] font-semibold text-slate-950">Audit indisponible</p>
        </div>
        <p className="m-0 text-[0.84rem] leading-6 text-slate-600">{auditError}</p>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="grid gap-3 rounded-[4px] border border-dashed border-slate-200 bg-slate-50/85 px-4 py-4">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-[4px] bg-white text-slate-600 shadow-[0_8px_18px_rgba(15,23,42,0.08)]">
            <History aria-hidden="true" size={18} />
          </span>
          <div className="grid gap-1">
            <p className="m-0 text-[0.92rem] font-semibold tracking-[-0.02em] text-slate-950">Aucun événement récent</p>
            <p className="m-0 text-[0.82rem] leading-6 text-slate-600">Les prochains changements de rôle et refus de garde RBAC apparaîtront ici.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-2.5">
        <div className="grid gap-0.5">
          <p className="m-0 text-[0.84rem] font-semibold tracking-[-0.02em] text-slate-950">Journal d’accès RBAC</p>
          <p className="m-0 text-[0.76rem] leading-5 text-slate-600">Historique récent des changements de rôle et des refus de garde critiques côté backend.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
            pillToneClasses.outline,
          ])}>
            {items.length} événement(s)
          </span>
          {isLoading ? (
            <span className={joinClasses([
              "inline-flex items-center gap-1 rounded-[4px] px-2.5 py-1 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
              pillToneClasses.info,
            ])}>
              <LoaderCircle aria-hidden="true" className="animate-spin" size={12} />
              Rechargement
            </span>
          ) : null}
        </div>
      </div>

      {auditError ? (
        <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName("danger")])}>
          {auditError}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[4px] border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-[0.82rem] text-slate-700">
            <thead className="bg-slate-50 text-left text-[0.68rem] uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Date / heure</th>
                <th className="px-3 py-2.5 font-semibold">Acteur</th>
                <th className="px-3 py-2.5 font-semibold">Cible</th>
                <th className="px-3 py-2.5 font-semibold">Événement</th>
                <th className="px-3 py-2.5 font-semibold">Résultat</th>
              </tr>
            </thead>
            <tbody>
              {items.map((entry) => {
                const actorLabel = resolveAccessAuditActorLabel(entry);
                const targetLabel = resolveAccessAuditTargetLabel(entry);
                const roleTransition = buildAccessAuditRoleTransition(entry);
                const showActorUserId = entry?.actor_email_snapshot && entry?.actor_user_id && entry.actor_email_snapshot !== entry.actor_user_id;
                const showTargetUserId = entry?.target_email_snapshot && entry?.target_user_id && entry.target_email_snapshot !== entry.target_user_id;

                return (
                  <tr key={entry.audit_id} className="border-t border-slate-200 align-top">
                    <td className="px-3 py-3 text-[0.78rem] text-slate-600">{formatDateTime(entry.occurred_at)}</td>
                    <td className="px-3 py-3">
                      <div className="grid gap-0.5">
                        <p className="m-0 font-semibold tracking-[-0.02em] text-slate-950">{actorLabel}</p>
                        {showActorUserId ? <p className="m-0 text-[0.72rem] leading-5 text-slate-500">{entry.actor_user_id}</p> : null}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="grid gap-0.5">
                        <p className="m-0 font-semibold tracking-[-0.02em] text-slate-950">{targetLabel}</p>
                        {showTargetUserId ? <p className="m-0 text-[0.72rem] leading-5 text-slate-500">{entry.target_user_id}</p> : null}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="grid gap-0.5">
                        <p className="m-0 font-semibold tracking-[-0.02em] text-slate-950">{entry.event_label || entry.event_key}</p>
                        {roleTransition ? <p className="m-0 text-[0.72rem] leading-5 text-slate-500">{roleTransition}</p> : null}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={joinClasses([
                        "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.64rem] font-bold uppercase tracking-[0.12em]",
                        resolveAuditOutcomeBadgeClassName(entry.outcome),
                      ])}>
                        {resolveAuditOutcomeLabel(entry.outcome)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function SystemConfigAccessSection({ access }) {
  const { reload: reloadGlobalAccess } = useAccess();
  const canSeeAccessManagement = canAccessAccessManagement(access);
  const canReadAudit = canReadAccessAudit(access);
  const canManageUsers = canManageAccessUsers(access);
  const canReadCatalog = canReadAccessCatalog(access);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(canManageUsers ? "users" : canReadCatalog ? "roles" : "audit");
  const [usersState, setUsersState] = React.useState(null);
  const [rolesState, setRolesState] = React.useState(null);
  const [permissionState, setPermissionState] = React.useState(null);
  const [auditState, setAuditState] = React.useState(null);
  const sharedIdentityConfirmationRequired = usersState?.directory?.shared_identity_confirmation_required === true;
  const [draftRolesByUserId, setDraftRolesByUserId] = React.useState({});
  const [createUserState, setCreateUserState] = React.useState(buildAccessUserCreationState());
  const [manualAssignment, setManualAssignment] = React.useState(buildManualRoleAssignmentState());
  const [selectedRoleKey, setSelectedRoleKey] = React.useState("");
  const [roleDraft, setRoleDraft] = React.useState(null);
  const [rolePermissionSearch, setRolePermissionSearch] = React.useState("");
  const [createRoleState, setCreateRoleState] = React.useState(null);
  const [createUserConfirmation, setCreateUserConfirmation] = React.useState(null);
  const [passwordChangeConfirmation, setPasswordChangeConfirmation] = React.useState(null);
  const [passwordResetEmailConfirmation, setPasswordResetEmailConfirmation] = React.useState(null);
  const [removeRoleConfirmation, setRemoveRoleConfirmation] = React.useState(null);
  const [suspendAccessConfirmation, setSuspendAccessConfirmation] = React.useState(null);
  const [roleAssignConfirmation, setRoleAssignConfirmation] = React.useState(null);
  const [roleSaveConfirmation, setRoleSaveConfirmation] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAuditLoading, setIsAuditLoading] = React.useState(false);
  const [isCreatingUser, setIsCreatingUser] = React.useState(false);
  const [isManualSubmitting, setIsManualSubmitting] = React.useState(false);
  const [isCreatingRole, setIsCreatingRole] = React.useState(false);
  const [isSavingRole, setIsSavingRole] = React.useState(false);
  const [isArchivingRole, setIsArchivingRole] = React.useState(false);
  const [isRemovingUserId, setIsRemovingUserId] = React.useState("");
  const [isSavingUserId, setIsSavingUserId] = React.useState("");
  const [isSendingResetEmailUserId, setIsSendingResetEmailUserId] = React.useState("");
  const [isTogglingDriverPortalUserId, setIsTogglingDriverPortalUserId] = React.useState("");
  const [isUpdatingPasswordUserId, setIsUpdatingPasswordUserId] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const [auditError, setAuditError] = React.useState("");
  const [feedback, setFeedback] = React.useState(null);

  const tabs = React.useMemo(() => {
    const nextTabs = [];
    if (canManageUsers) {
      nextTabs.push({ key: "users", label: "Utilisateurs", icon: Users });
    }
    if (canReadCatalog) {
      nextTabs.push({ key: "roles", label: "Rôles", icon: Shield });
      nextTabs.push({ key: "permissions", label: "Permissions", icon: KeyRound });
    }
    if (canReadAudit) {
      nextTabs.push({ key: "audit", label: "Audit", icon: History });
    }
    return nextTabs;
  }, [canManageUsers, canReadAudit, canReadCatalog]);

  const roles = Array.isArray(rolesState?.roles) ? rolesState.roles : [];
  const assignableRoles = React.useMemo(() => roles.filter((role) => role?.is_active !== false), [roles]);
  const permissions = Array.isArray(permissionState?.groups) ? permissionState.groups : [];
  const selectedRole = roles.find((role) => role.role_key === selectedRoleKey) || roles[0] || null;

  const loadAccessState = React.useCallback(async ({ quiet = false } = {}) => {
    if (!canSeeAccessManagement) {
      return;
    }

    if (!quiet) {
      setIsLoading(true);
    }
    setLoadError("");

    try {
      const [nextUsersState, nextRolesState, nextPermissionState] = await Promise.all([
        canManageUsers ? fetchAccessUsers() : Promise.resolve({ users: [], directory: { source: "rbac_assignments_only", warning: null } }),
        canReadCatalog ? fetchAccessRoles() : Promise.resolve({ roles: [] }),
        canReadCatalog ? fetchAccessPermissionCatalog() : Promise.resolve({ groups: [] }),
      ]);

      setUsersState(nextUsersState);
      setRolesState(nextRolesState);
      setPermissionState(nextPermissionState);
      setDraftRolesByUserId(buildRoleDraftMap(nextUsersState?.users));
      const nextRoles = Array.isArray(nextRolesState?.roles) ? nextRolesState.roles : [];
      const nextAssignableRoles = nextRoles.filter((role) => role?.is_active !== false);
      setCreateUserState((currentState) => ({
        ...currentState,
        roleKey: resolveAccessRoleKey(currentState.roleKey, nextAssignableRoles),
      }));
      setManualAssignment((currentState) => ({
        ...currentState,
        roleKey: resolveAccessRoleKey(currentState.roleKey, nextAssignableRoles),
      }));
      setSelectedRoleKey((currentRoleKey) => resolveAccessRoleKey(currentRoleKey, nextRoles));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "Impossible de charger la gestion des accès.");
    } finally {
      if (!quiet) {
        setIsLoading(false);
      }
    }
  }, [canManageUsers, canReadCatalog, canSeeAccessManagement]);

  const loadAccessAudit = React.useCallback(async ({ quiet = false } = {}) => {
    if (!canReadAudit) {
      setAuditState(null);
      setAuditError("");
      setIsAuditLoading(false);
      return;
    }

    if (!quiet) {
      setIsAuditLoading(true);
    }
    setAuditError("");

    try {
      const nextAuditState = await fetchAccessAuditEvents();
      setAuditState(nextAuditState);
    } catch (error) {
      setAuditError(error instanceof Error ? error.message : "Impossible de charger l’audit d’accès.");
    } finally {
      if (!quiet) {
        setIsAuditLoading(false);
      }
    }
  }, [canReadAudit]);

  React.useEffect(() => {
    if (!canSeeAccessManagement) {
      return undefined;
    }

    void loadAccessState();
    if (canReadAudit) {
      void loadAccessAudit();
    }
    return undefined;
  }, [canReadAudit, canSeeAccessManagement, loadAccessAudit, loadAccessState]);

  React.useEffect(() => {
    if (!tabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(tabs[0]?.key || "audit");
    }
  }, [activeTab, tabs]);

  React.useEffect(() => {
    setSelectedRoleKey((currentRoleKey) => resolveAccessRoleKey(currentRoleKey, roles));
  }, [roles]);

  React.useEffect(() => {
    const nextSelectedRole = roles.find((role) => role.role_key === selectedRoleKey) || roles[0] || null;
    setRoleDraft(nextSelectedRole ? buildRoleEditorDraft(nextSelectedRole) : null);
    setRolePermissionSearch("");
  }, [roles, selectedRoleKey]);

  const handleReload = React.useCallback(() => {
    setFeedback(null);
    void loadAccessState();
    void reloadGlobalAccess();
    if (canReadAudit) {
      void loadAccessAudit();
    }
  }, [canReadAudit, loadAccessAudit, loadAccessState, reloadGlobalAccess]);

  const handleDraftRoleChange = React.useCallback((userId, nextRole) => {
    setDraftRolesByUserId((currentMap) => ({
      ...currentMap,
      [userId]: nextRole,
    }));
  }, []);

  const handleManualAssignmentChange = React.useCallback((field, value) => {
    setManualAssignment((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  }, []);

  const handleCreateUserChange = React.useCallback((field, value) => {
    setCreateUserState((currentState) => ({
      ...currentState,
      [field]: value,
    }));
  }, []);

  const handleRolePermissionSearchChange = React.useCallback((value) => {
    setRolePermissionSearch(value);
  }, []);

  const handleRoleDraftChange = React.useCallback((field, value) => {
    setRoleDraft((currentDraft) => (currentDraft ? { ...currentDraft, [field]: value } : currentDraft));
  }, []);

  const handleRolePermissionToggle = React.useCallback((permissionKey) => {
    setRoleDraft((currentDraft) => {
      if (!currentDraft) {
        return currentDraft;
      }

      const linkedPermissionKeys = resolveLinkedRolePermissionKeys(permissionKey);
      const nextPermissionKeySet = new Set(currentDraft.permissionKeys || []);

      if (linkedPermissionKeys) {
        const isFullySelected = linkedPermissionKeys.every((candidate) => nextPermissionKeySet.has(candidate));
        linkedPermissionKeys.forEach((candidate) => {
          if (isFullySelected) {
            nextPermissionKeySet.delete(candidate);
          } else {
            nextPermissionKeySet.add(candidate);
          }
        });
      } else {
        if (nextPermissionKeySet.has(permissionKey)) {
          nextPermissionKeySet.delete(permissionKey);
        } else {
          nextPermissionKeySet.add(permissionKey);
        }
      }

      return {
        ...currentDraft,
        permissionKeys: normalizeRolePermissionKeys(Array.from(nextPermissionKeySet)),
      };
    });
  }, []);

  const handleRoleDraftReset = React.useCallback(() => {
    setRoleDraft(selectedRole ? buildRoleEditorDraft(selectedRole) : null);
  }, [selectedRole]);

  const handleOpenCreateRole = React.useCallback(() => {
    const baseRoleKey = selectedRole?.is_active ? selectedRole.role_key : assignableRoles[0]?.role_key || "";
    setCreateRoleState(buildRoleCloneState(baseRoleKey));
  }, [assignableRoles, selectedRole]);

  const handleCreateRoleCancel = React.useCallback(() => {
    setCreateRoleState(null);
  }, []);

  const handleOpenPasswordChange = React.useCallback((user) => {
    if (!user?.user_id) {
      return;
    }

    setPasswordChangeConfirmation(buildAccessUserPasswordConfirmationModel(user));
  }, []);

  const handleCancelPasswordChange = React.useCallback(() => {
    if (isUpdatingPasswordUserId) {
      return;
    }

    setPasswordChangeConfirmation(null);
  }, [isUpdatingPasswordUserId]);

  const handleCreateRoleChange = React.useCallback((field, value) => {
    setCreateRoleState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const nextState = {
        ...currentState,
        [field]: field === "roleKey" ? slugifyRoleKey(value) : value,
      };

      if (field === "label") {
        const previousSuggestedKey = slugifyRoleKey(currentState.label);
        if (!currentState.roleKey || currentState.roleKey === previousSuggestedKey) {
          nextState.roleKey = slugifyRoleKey(value);
        }
      }

      return nextState;
    });
  }, []);

  const handleApplyRoleChange = React.useCallback((user) => {
    if (isProtectedAccessAdminUser(user)) {
      setFeedback({
        tone: "danger",
        message: "Le compte admin principal doit conserver le rôle Admin.",
      });
      return;
    }

    if (isAccessUserSuspended(user)) {
      setFeedback({
        tone: "info",
        message: "Choisissez le rôle à restaurer puis utilisez Réactiver pour lever le ban Supabase.",
      });
      return;
    }

    const nextRole = resolveAccessRoleKey(draftRolesByUserId[user.user_id] || user.active_role, assignableRoles);
    const canRepairUidMismatch = user?.status === "uid_mismatch";
    if (!nextRole || (nextRole === user.active_role && user.has_active_assignment && !canRepairUidMismatch) || isSavingUserId || roleAssignConfirmation) {
      return;
    }

    const nextRoleLabel = assignableRoles.find((role) => role.role_key === nextRole)?.label || nextRole;
    setRoleAssignConfirmation(buildAccessUserRoleAssignConfirmationModel({
      kind: "table",
      nextRoleKey: nextRole,
      nextRoleLabel,
      targetLabel: user.email || user.user_id,
      user,
    }));
  }, [assignableRoles, draftRolesByUserId, isSavingUserId, roleAssignConfirmation]);

  const handleCancelRoleAssignConfirmation = React.useCallback(() => {
    if (isSavingUserId || isManualSubmitting) {
      return;
    }

    setRoleAssignConfirmation(null);
  }, [isManualSubmitting, isSavingUserId]);

  const handleConfirmRoleAssign = React.useCallback(async () => {
    if (!roleAssignConfirmation || isSavingUserId || isManualSubmitting) {
      return;
    }

    const confirmation = roleAssignConfirmation;
    setRoleAssignConfirmation(null);
    setFeedback(null);

    if (confirmation.kind === "manual") {
      const resolvedUserId = String(confirmation.userId || "").trim();
      const resolvedEmail = String(confirmation.email || "").trim();
      const targetLabel = resolvedUserId || resolvedEmail || confirmation.nextRoleLabel;

      setIsManualSubmitting(true);
      try {
        await assignAccessUserRole({
          user_id: resolvedUserId || undefined,
          email: resolvedEmail || undefined,
          role_key: confirmation.nextRoleKey,
        });
        setFeedback({
          tone: "success",
          message: `Rôle ${confirmation.nextRoleLabel} attribué à ${targetLabel}.`,
        });
        setManualAssignment(buildManualRoleAssignmentState(confirmation.nextRoleKey));
        await Promise.all([
          loadAccessState({ quiet: true }),
          reloadGlobalAccess(),
          canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
        ]);
      } catch (error) {
        setFeedback({
          tone: "danger",
          message: error instanceof Error ? error.message : "Impossible d’attribuer le rôle utilisateur.",
        });
      } finally {
        setIsManualSubmitting(false);
      }
      return;
    }

    const user = confirmation.user;
    if (!user?.user_id) {
      return;
    }

    const targetLabel = user.email || user.user_id;
    setIsSavingUserId(user.user_id);

    try {
      await updateAccessUserRole(user.user_id, {
        role_key: confirmation.nextRoleKey,
        email: user.email || undefined,
      });
      setFeedback({
        tone: "success",
        message: `Rôle mis à jour pour ${targetLabel}.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de mettre à jour le rôle utilisateur.",
      });
    } finally {
      setIsSavingUserId("");
    }
  }, [canReadAudit, isManualSubmitting, isSavingUserId, loadAccessAudit, loadAccessState, reloadGlobalAccess, roleAssignConfirmation]);

  const handleRemoveRole = React.useCallback((user) => {
    if (isProtectedAccessAdminUser(user)) {
      setFeedback({
        tone: "danger",
        message: "Le compte admin principal ne peut pas être supprimé.",
      });
      return;
    }

    if (isRemovingUserId || removeRoleConfirmation) {
      return;
    }

    setRemoveRoleConfirmation(buildAccessUserRemoveConfirmationModel(user, {
      sharedIdentityConfirmationRequired,
    }));
  }, [isRemovingUserId, removeRoleConfirmation, sharedIdentityConfirmationRequired]);

  const handleCancelRemoveRoleConfirmation = React.useCallback(() => {
    if (isRemovingUserId) {
      return;
    }

    setRemoveRoleConfirmation(null);
  }, [isRemovingUserId]);

  const handleConfirmRemoveRole = React.useCallback(async (confirmSharedIdentity = false) => {
    if (!removeRoleConfirmation?.user?.user_id || isRemovingUserId) {
      return;
    }

    const user = removeRoleConfirmation.user;
    if (removeRoleConfirmation.requiresSharedIdentityConfirmation && confirmSharedIdentity !== true) {
      setFeedback({
        tone: "danger",
        message: "Confirmez que cette suppression locale affectera aussi le compte en production.",
      });
      return;
    }
    if (!user.linked_truck_id && !hasSupabaseAccount(user)) {
      setRemoveRoleConfirmation(null);
      setFeedback({
        tone: "danger",
        message: "Ce compte ne possède pas de compte Supabase Auth supprimable.",
      });
      return;
    }

    setRemoveRoleConfirmation(null);

    setIsRemovingUserId(user.user_id);
    setFeedback(null);

    try {
      // Un lien camion hérité est effacé par la suppression normale (réconciliation côté serveur).
      await deleteAccessUser(user.user_id, { confirmSharedIdentity });
      setFeedback({
        tone: "success",
        message: resolveAccessUserRemoveSuccessMessage(user),
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error
          ? error.message
          : "Impossible de supprimer définitivement le compte Supabase Auth.",
      });
    } finally {
      setIsRemovingUserId("");
    }
  }, [canReadAudit, isRemovingUserId, loadAccessAudit, loadAccessState, reloadGlobalAccess, removeRoleConfirmation]);

  const executeToggleDriverPortalAccess = React.useCallback(async (
    user,
    { confirmSharedIdentity = false, roleKey = "" } = {},
  ) => {
    const targetUserId = user.user_id;
    const isDriverPortalUser = isDriverPortalLinkedUser(user);
    const isReactivateAction = isAccessUserSuspended(user);
    const selectedRoleKey = roleKey || resolveAccessUserRoleKey(user);
    const nextRoleKey = isDriverPortalUser
      ? DRIVER_ROLE_KEY
      : assignableRoles.length > 0
        ? resolveAccessRoleKey(selectedRoleKey, assignableRoles)
        : selectedRoleKey;
    const nextRoleLabel = assignableRoles.find((role) => role.role_key === nextRoleKey)?.label
      || user?.active_role_label
      || nextRoleKey;

    setIsTogglingDriverPortalUserId(targetUserId);
    setFeedback(null);

    try {
      if (isReactivateAction) {
        if (!nextRoleKey) {
          throw new Error("Aucun rôle actif n’est sélectionné pour réactiver ce compte.");
        }

        await reactivateAccessUser(targetUserId, {
          role_key: nextRoleKey,
        });
      } else {
        await suspendAccessUser(targetUserId, { confirmSharedIdentity });
      }

      setFeedback({
        tone: "success",
        message: isReactivateAction
          ? `Compte ${user?.email || user?.user_id || "utilisateur"} réactivé : le ban Supabase partagé est levé et le rôle ${nextRoleLabel} est restauré dans cet environnement.`
          : `Compte ${user?.email || user?.user_id || "utilisateur"} suspendu : la connexion Supabase partagée est bloquée dans tous les environnements, mais le compte reste réactivable.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error
          ? error.message
          : (isReactivateAction ? "Impossible de réactiver le compte utilisateur." : "Impossible de suspendre complètement le compte utilisateur."),
      });
    } finally {
      setIsTogglingDriverPortalUserId("");
    }
  }, [assignableRoles, canReadAudit, loadAccessAudit, loadAccessState, reloadGlobalAccess]);

  const handleToggleDriverPortalAccess = React.useCallback((user) => {
    if (isProtectedAccessAdminUser(user)) {
      setFeedback({
        tone: "danger",
        message: "Le compte admin principal ne peut pas être suspendu.",
      });
      return;
    }

    if (isTogglingDriverPortalUserId || suspendAccessConfirmation) {
      return;
    }

    const canToggleUser = canToggleAccessUser(user);
    if (!canToggleUser) {
      setFeedback({
        tone: "danger",
        message: "Aucun compte Supabase n’est disponible pour cette action.",
      });
      return;
    }

    const isDriverPortalUser = isDriverPortalLinkedUser(user);
    const preservesActiveRole = isAccessUserSuspended(user) && user?.has_active_assignment === true;
    const selectedRoleKey = isDriverPortalUser
      ? DRIVER_ROLE_KEY
      : preservesActiveRole
        ? resolveAccessUserRoleKey(user)
      : resolveAccessRoleKey(
        draftRolesByUserId[user.user_id] || resolveAccessUserRoleKey(user),
        assignableRoles,
      );
    const selectedRoleLabel = isDriverPortalUser
      ? assignableRoles.find((role) => role.role_key === DRIVER_ROLE_KEY)?.label || "Chauffeur"
      : assignableRoles.find((role) => role.role_key === selectedRoleKey)?.label || selectedRoleKey;
    setSuspendAccessConfirmation(buildAccessUserSuspendConfirmationModel(user, {
      sharedIdentityConfirmationRequired,
      roleKey: selectedRoleKey,
      roleLabel: selectedRoleLabel,
    }));
  }, [assignableRoles, draftRolesByUserId, isTogglingDriverPortalUserId, sharedIdentityConfirmationRequired, suspendAccessConfirmation]);

  const handleCancelSuspendAccessConfirmation = React.useCallback(() => {
    if (isTogglingDriverPortalUserId) {
      return;
    }

    setSuspendAccessConfirmation(null);
  }, [isTogglingDriverPortalUserId]);

  const handleConfirmSuspendAccess = React.useCallback(async (confirmSharedIdentity = false) => {
    if (!suspendAccessConfirmation?.user?.user_id || isTogglingDriverPortalUserId) {
      return;
    }

    const user = suspendAccessConfirmation.user;
    const roleKey = suspendAccessConfirmation.roleKey;
    if (suspendAccessConfirmation.requiresSharedIdentityConfirmation && confirmSharedIdentity !== true) {
      setFeedback({
        tone: "danger",
        message: "Confirmez que cette suspension locale affectera aussi le compte en production.",
      });
      return;
    }
    setSuspendAccessConfirmation(null);
    await executeToggleDriverPortalAccess(user, { confirmSharedIdentity, roleKey });
  }, [executeToggleDriverPortalAccess, isTogglingDriverPortalUserId, suspendAccessConfirmation]);

  const handleManualAssignmentSubmit = React.useCallback(() => {
    if (isManualSubmitting || roleAssignConfirmation) {
      return;
    }

    const resolvedUserId = manualAssignment.userId.trim();
    const resolvedEmail = manualAssignment.email.trim();
    const resolvedRoleKey = resolveAccessRoleKey(manualAssignment.roleKey, assignableRoles);

    if (!resolvedUserId && !resolvedEmail) {
      setFeedback({
        tone: "danger",
        message: "Renseignez un UID Supabase ou un email avant d’appliquer le rôle.",
      });
      return;
    }

    if (!resolvedRoleKey) {
      setFeedback({
        tone: "danger",
        message: "Aucun rôle actif n’est disponible pour cette affectation.",
      });
      return;
    }

    const nextRoleLabel = assignableRoles.find((role) => role.role_key === resolvedRoleKey)?.label || resolvedRoleKey;
    const targetLabel = resolvedUserId || resolvedEmail;
    setRoleAssignConfirmation(buildAccessUserRoleAssignConfirmationModel({
      email: resolvedEmail,
      kind: "manual",
      nextRoleKey: resolvedRoleKey,
      nextRoleLabel,
      targetLabel,
      userId: resolvedUserId,
    }));
  }, [assignableRoles, isManualSubmitting, manualAssignment.email, manualAssignment.roleKey, manualAssignment.userId, roleAssignConfirmation]);

  const handleCreateUserSubmit = React.useCallback(() => {
    if (isCreatingUser) {
      return;
    }

    const resolvedEmail = createUserState.email.trim().toLowerCase();
    const resolvedPassword = createUserState.password.trim();
    const resolvedFullName = createUserState.fullName.trim();
    const resolvedRoleKey = resolveAccessRoleKey(createUserState.roleKey, assignableRoles);

    if (!resolvedEmail) {
      setFeedback({
        tone: "danger",
        message: "Renseignez un email de connexion avant de créer le compte.",
      });
      return;
    }

    if (resolvedPassword.length < 8) {
      setFeedback({
        tone: "danger",
        message: "Le mot de passe initial doit contenir au moins 8 caractères.",
      });
      return;
    }

    if (!resolvedRoleKey) {
      setFeedback({
        tone: "danger",
        message: "Aucun rôle actif n’est disponible pour cette création.",
      });
      return;
    }

    const nextRoleLabel = assignableRoles.find((role) => role.role_key === resolvedRoleKey)?.label || resolvedRoleKey;

    setFeedback(null);
    setCreateUserConfirmation(buildAccessUserCreationConfirmationModel({
      email: resolvedEmail,
      fullName: resolvedFullName,
      password: resolvedPassword,
      roleKey: resolvedRoleKey,
      roleLabel: nextRoleLabel,
    }));
  }, [assignableRoles, createUserState.email, createUserState.fullName, createUserState.password, createUserState.roleKey, isCreatingUser]);

  const handleCancelCreateUserConfirmation = React.useCallback(() => {
    if (isCreatingUser) {
      return;
    }

    setCreateUserConfirmation(null);
  }, [isCreatingUser]);

  const handleConfirmCreateUser = React.useCallback(async () => {
    if (!createUserConfirmation?.payload || isCreatingUser) {
      return;
    }

    const {
      email,
      fullName,
      password,
      roleKey,
      roleLabel,
    } = createUserConfirmation.payload;

    setIsCreatingUser(true);
    setFeedback(null);

    try {
      const result = await createAccessUser({
        email,
        password,
        role_key: roleKey,
        full_name: fullName || undefined,
      });
      setFeedback({
        tone: "success",
        message: `Compte ${result.email} créé et rôle ${result.active_role_label || roleLabel} attribué.`,
      });
      setCreateUserState(buildAccessUserCreationState(roleKey));
      setCreateUserConfirmation(null);
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de créer le compte utilisateur.",
      });
    } finally {
      setIsCreatingUser(false);
    }
  }, [canReadAudit, createUserConfirmation, isCreatingUser, loadAccessAudit, loadAccessState, reloadGlobalAccess]);

  const handleConfirmPasswordChange = React.useCallback(async ({ textPromptValue } = {}) => {
    if (!passwordChangeConfirmation?.user?.user_id || isUpdatingPasswordUserId) {
      return;
    }

    const resolvedPassword = String(textPromptValue || "").trim();
    if (resolvedPassword.length < 8) {
      setPasswordChangeConfirmation((currentState) => (currentState ? {
        ...currentState,
        warningMessage: "Le mot de passe doit contenir au moins 8 caractères.",
      } : currentState));
      return;
    }

    const targetUser = passwordChangeConfirmation.user;
    const targetLabel = targetUser.email || targetUser.user_id;
    setIsUpdatingPasswordUserId(targetUser.user_id);
    setFeedback(null);

    try {
      await updateAccessUserPassword(targetUser.user_id, {
        email: targetUser.email || undefined,
        password: resolvedPassword,
      });
      setPasswordChangeConfirmation(null);
      setFeedback({
        tone: "success",
        message: `Mot de passe mis à jour pour ${targetLabel}.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setPasswordChangeConfirmation((currentState) => (currentState ? {
        ...currentState,
        warningMessage: error instanceof Error ? error.message : "Impossible de mettre à jour le mot de passe utilisateur.",
      } : currentState));
    } finally {
      setIsUpdatingPasswordUserId("");
    }
  }, [canReadAudit, isUpdatingPasswordUserId, loadAccessAudit, loadAccessState, passwordChangeConfirmation, reloadGlobalAccess]);

  const handleSendPasswordResetEmail = React.useCallback((user) => {
    if (isSendingResetEmailUserId) {
      return;
    }

    const resolvedUserId = String(user?.user_id || "").trim();
    const resolvedEmail = String(user?.email || "").trim().toLowerCase();
    if (!resolvedUserId) {
      setFeedback({
        tone: "danger",
        message: "Aucun identifiant utilisateur n’est disponible pour cet utilisateur.",
      });
      return;
    }
    if (!resolvedEmail) {
      setFeedback({
        tone: "danger",
        message: "Aucun courriel n’est disponible pour cet utilisateur.",
      });
      return;
    }

    setPasswordResetEmailConfirmation(buildAccessUserPasswordResetEmailConfirmationModel(user));
  }, [isSendingResetEmailUserId]);

  const handleCancelPasswordResetEmail = React.useCallback(() => {
    if (isSendingResetEmailUserId) {
      return;
    }

    setPasswordResetEmailConfirmation(null);
  }, [isSendingResetEmailUserId]);

  const handleConfirmPasswordResetEmail = React.useCallback(async () => {
    if (!passwordResetEmailConfirmation?.user?.user_id || isSendingResetEmailUserId) {
      return;
    }

    const targetUser = passwordResetEmailConfirmation.user;
    const resolvedUserId = String(targetUser.user_id || "").trim();
    const resolvedEmail = String(targetUser.email || "").trim().toLowerCase();
    if (!resolvedUserId || !resolvedEmail) {
      setPasswordResetEmailConfirmation((currentState) => (currentState ? {
        ...currentState,
        errorMessage: "Les informations utilisateur sont incomplètes pour envoyer le lien.",
        warningMessage: "",
      } : currentState));
      return;
    }

    setIsSendingResetEmailUserId(resolvedUserId);
    setFeedback(null);

    try {
      await sendAccessUserPasswordResetEmail(resolvedUserId);
      setPasswordResetEmailConfirmation(null);
      setFeedback({
        tone: "success",
        message: `Un e-mail de réinitialisation a été envoyé à ${resolvedEmail}.`,
      });
    } catch (error) {
      setPasswordResetEmailConfirmation((currentState) => (currentState ? {
        ...currentState,
        errorMessage: formatPasswordResetEmailError(error),
        warningMessage: "",
      } : currentState));
    } finally {
      setIsSendingResetEmailUserId("");
    }
  }, [isSendingResetEmailUserId, passwordResetEmailConfirmation]);

  const handleCreateRoleSubmit = React.useCallback(async () => {
    if (!createRoleState || isCreatingRole) {
      return;
    }

    const resolvedBaseRoleKey = resolveAccessRoleKey(createRoleState.baseRoleKey, assignableRoles);
    const resolvedLabel = createRoleState.label.trim();
    const resolvedRoleKey = slugifyRoleKey(createRoleState.roleKey || createRoleState.label);
    const resolvedDescription = createRoleState.description.trim();

    if (!resolvedBaseRoleKey || !resolvedLabel || !resolvedRoleKey) {
      setFeedback({
        tone: "danger",
        message: "Renseignez un rôle source, un libellé et une clé technique avant de créer le rôle custom.",
      });
      return;
    }

    if (!await confirmInGrain(`Créer le rôle ${resolvedLabel} par clonage de ${resolvedBaseRoleKey} ?`)) return;

    setIsCreatingRole(true);
    setFeedback(null);

    try {
      const createdRole = await createAccessRole({
        base_role_key: resolvedBaseRoleKey,
        description: resolvedDescription || undefined,
        label: resolvedLabel,
        role_key: resolvedRoleKey,
      });
      setCreateRoleState(null);
      setSelectedRoleKey(createdRole.role_key);
      setFeedback({
        tone: "success",
        message: `Rôle ${createdRole.label} créé par clonage.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible de créer le rôle custom.",
      });
    } finally {
      setIsCreatingRole(false);
    }
  }, [assignableRoles, canReadAudit, createRoleState, isCreatingRole, loadAccessAudit, loadAccessState, reloadGlobalAccess]);

  const handleCancelRoleSaveConfirmation = React.useCallback(() => {
    if (isSavingRole) {
      return;
    }
    setRoleSaveConfirmation(null);
  }, [isSavingRole]);

  const handleConfirmRoleSave = React.useCallback(async () => {
    if (!roleSaveConfirmation || isSavingRole) {
      return;
    }

    setRoleSaveConfirmation(null);
    setIsSavingRole(true);
    setFeedback(null);

    try {
      if (roleSaveConfirmation.hasDefinitionChange) {
        await updateAccessRole(roleSaveConfirmation.roleKey, {
          description: roleSaveConfirmation.nextDescription || undefined,
          label: roleSaveConfirmation.nextLabel,
        });
      }

      if (!compareStringArrays(roleSaveConfirmation.currentPermissionKeys, roleSaveConfirmation.nextPermissionKeys)) {
        await replaceAccessRolePermissions(roleSaveConfirmation.roleKey, {
          permission_keys: roleSaveConfirmation.nextPermissionKeys,
        });
      }

      setFeedback({
        tone: "success",
        message: `Rôle ${roleSaveConfirmation.nextLabel} enregistré.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible d’enregistrer le rôle.",
      });
    } finally {
      setIsSavingRole(false);
    }
  }, [canReadAudit, isSavingRole, loadAccessAudit, loadAccessState, reloadGlobalAccess, roleSaveConfirmation]);

  const handleSaveRole = React.useCallback(() => {
    if (!selectedRole || !roleDraft || !isRoleDraftDirty(selectedRole, roleDraft) || isSavingRole) {
      return;
    }

    const nextLabel = roleDraft.label.trim();
    const nextDescription = roleDraft.description.trim();
    const nextPermissionKeys = normalizeRolePermissionKeys(roleDraft.permissionKeys || []);
    const currentPermissionKeys = normalizeRolePermissionKeys(
      Array.isArray(selectedRole.permissions)
        ? selectedRole.permissions.map((permission) => permission.permission_key).filter(Boolean)
        : [],
    );

    if (!nextLabel) {
      setFeedback({
        tone: "danger",
        message: "Le libellé du rôle ne peut pas être vide.",
      });
      return;
    }

    setRoleSaveConfirmation(buildRoleSaveConfirmationModel({
      currentPermissionKeys,
      nextDescription,
      nextLabel,
      nextPermissionKeys,
      role: selectedRole,
    }));
  }, [isSavingRole, roleDraft, selectedRole]);

  const handleArchiveRole = React.useCallback(async () => {
    if (!selectedRole || selectedRole.is_system || !selectedRole.is_active || isArchivingRole) {
      return;
    }

    if (!await confirmInGrain(`Archiver le rôle ${selectedRole.label} ?`)) return;

    setIsArchivingRole(true);
    setFeedback(null);

    try {
      await archiveAccessRole(selectedRole.role_key);
      setFeedback({
        tone: "success",
        message: `Rôle ${selectedRole.label} archivé.`,
      });
      await Promise.all([
        loadAccessState({ quiet: true }),
        reloadGlobalAccess(),
        canReadAudit ? loadAccessAudit({ quiet: true }) : Promise.resolve(),
      ]);
    } catch (error) {
      setFeedback({
        tone: "danger",
        message: error instanceof Error ? error.message : "Impossible d’archiver le rôle.",
      });
    } finally {
      setIsArchivingRole(false);
    }
  }, [canReadAudit, isArchivingRole, loadAccessAudit, loadAccessState, reloadGlobalAccess, selectedRole]);

  if (!canSeeAccessManagement) {
    return null;
  }

  const hasLoadedState = Boolean(usersState) || Boolean(rolesState) || Boolean(permissionState) || Boolean(auditState);

  return (
    <>
      <section className="access-section grid gap-2 rounded-[4px] border border-black/6 bg-white px-3 py-2 shadow-[0_8px_20px_rgba(15,23,42,0.04)] max-[720px]:px-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <p className="m-0 text-[0.9rem] font-semibold tracking-[-0.025em] text-slate-950">Accès et permissions</p>
            {Array.isArray(usersState?.users) ? (
              <span className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.1em]",
                pillToneClasses.outline,
              ])}>
                {usersState.users.length} utilisateur(s)
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              aria-expanded={isHelpDialogOpen}
              aria-haspopup="dialog"
              aria-label="Ouvrir l’aide RBAC"
              className={joinClasses([
                "inline-flex items-center justify-center gap-1.5 rounded-[4px] border px-2.5 text-[0.74rem] font-semibold transition",
                buttonSizeClasses.sm,
                buttonToneClasses.ghost,
              ])}
              onClick={() => setIsHelpDialogOpen(true)}
              type="button"
            >
              <Info aria-hidden="true" size={14} />
              <span className="hidden min-[960px]:inline">Aide RBAC</span>
            </button>
            <button className={SECONDARY_ACTION_CLASS_NAME} disabled={isLoading && !hasLoadedState} onClick={handleReload} type="button">
              <RefreshCcw aria-hidden="true" size={14} />
              Recharger
            </button>
          </div>
        </div>

        {loadError ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName("danger")])}>
            {loadError}
          </div>
        ) : null}

        {feedback ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName(feedback.tone)])}>
            {feedback.message}
          </div>
        ) : null}

        {usersState?.directory?.warning ? (
          <div className={joinClasses(["rounded-[4px] px-3.5 py-3 text-[0.86rem] font-medium", resolveFeedbackToneClassName("info")])}>
            {usersState.directory.warning}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          {tabs.map((tab) => (
            <AccessTabButton
              active={activeTab === tab.key}
              icon={tab.icon}
              key={tab.key}
              label={tab.label}
              onClick={() => setActiveTab(tab.key)}
            />
          ))}
        </div>

        {isLoading && !hasLoadedState ? (
          <div className="flex min-h-40 items-center justify-center rounded-[4px] border border-slate-200 bg-slate-50/70 px-4 py-6 text-slate-600">
            <div className="flex items-center gap-3">
              <LoaderCircle aria-hidden="true" className="animate-spin" size={18} />
              <span className="text-[0.92rem] font-medium">Chargement des accès et permissions…</span>
            </div>
          </div>
        ) : null}

        {!isLoading || hasLoadedState ? (
          <div className="access-tab-content">
            {activeTab === "users" ? (
              <AccessUsersPanel
                createUserState={createUserState}
                currentUserId={access?.userId || ""}
                draftRolesByUserId={draftRolesByUserId}
                isCreatingUser={isCreatingUser}
                isManualSubmitting={isManualSubmitting}
                isRemovingUserId={isRemovingUserId}
                isSavingUserId={isSavingUserId}
                isSendingResetEmailUserId={isSendingResetEmailUserId}
                isTogglingDriverPortalUserId={isTogglingDriverPortalUserId}
                isUpdatingPasswordUserId={isUpdatingPasswordUserId}
                manualAssignment={manualAssignment}
                onApplyRoleChange={handleApplyRoleChange}
                onCreateUserChange={handleCreateUserChange}
                onCreateUserSubmit={handleCreateUserSubmit}
                onDraftRoleChange={handleDraftRoleChange}
                onManualAssignmentChange={handleManualAssignmentChange}
                onManualAssignmentSubmit={handleManualAssignmentSubmit}
                onOpenPasswordChange={handleOpenPasswordChange}
                onRemoveRole={handleRemoveRole}
                onSendPasswordResetEmail={handleSendPasswordResetEmail}
                onToggleDriverPortalAccess={handleToggleDriverPortalAccess}
                roles={assignableRoles}
                usersState={usersState}
              />
            ) : null}

            {activeTab === "roles" ? (
              <AccessRolesPanel
                createRoleState={createRoleState}
                isArchivingRole={isArchivingRole}
                isCreatingRole={isCreatingRole}
                isSavingRole={isSavingRole}
                onArchiveRole={handleArchiveRole}
                onCreateRoleCancel={handleCreateRoleCancel}
                onCreateRoleChange={handleCreateRoleChange}
                onCreateRoleSubmit={handleCreateRoleSubmit}
                onOpenCreateRole={handleOpenCreateRole}
                onRoleDraftChange={handleRoleDraftChange}
                onRoleDraftReset={handleRoleDraftReset}
                onRolePermissionSearchChange={handleRolePermissionSearchChange}
                onRolePermissionToggle={handleRolePermissionToggle}
                onSaveRole={handleSaveRole}
                onSelectRole={setSelectedRoleKey}
                permissionGroups={permissions}
                roleDraft={roleDraft}
                rolePermissionSearch={rolePermissionSearch}
                roles={roles}
                selectedRoleKey={selectedRoleKey}
              />
            ) : null}
            {activeTab === "permissions" ? <AccessPermissionsPanel groups={permissions} /> : null}
            {activeTab === "audit" ? <AccessAuditPanel auditError={auditError} auditState={auditState} isLoading={isAuditLoading} /> : null}

            {!hasLoadedState && !loadError ? (
              <div className="rounded-[4px] border border-dashed border-slate-200 bg-slate-50 px-4 py-4 text-[0.84rem] leading-6 text-slate-600">
                Cette sous-section n’a pas encore reçu de données exploitables.
              </div>
            ) : null}
          </div>
        ) : null}

        {loadError && !hasLoadedState ? (
          <div className="grid gap-3 rounded-[4px] border border-border-danger-subtle bg-white px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
            <div className="flex items-center gap-3 text-danger">
              <AlertTriangle aria-hidden="true" size={18} />
              <p className="m-0 text-[0.95rem] font-semibold text-slate-950">Chargement incomplet</p>
            </div>
            <p className="m-0 text-[0.84rem] leading-6 text-slate-600">La sous-section Accès et permissions n’a pas pu être initialisée. Utilisez Recharger pour retenter la lecture.</p>
          </div>
        ) : null}
      </section>

      <SystemConfigAccessHelpDialog isOpen={isHelpDialogOpen} onClose={() => setIsHelpDialogOpen(false)} />

      {createUserConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmLabel={isCreatingUser ? "Création..." : createUserConfirmation.confirmLabel}
          description={createUserConfirmation.description}
          eyebrow="Création"
          impactItems={createUserConfirmation.impactItems}
          onCancel={handleCancelCreateUserConfirmation}
          onClose={handleCancelCreateUserConfirmation}
          onConfirm={() => {
            void handleConfirmCreateUser();
          }}
          summaryDensity={createUserConfirmation.summaryDensity}
          summaryItems={createUserConfirmation.summaryItems}
          title={createUserConfirmation.title}
          tone={createUserConfirmation.tone}
        />
      ) : null}

      {removeRoleConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmCheckbox={removeRoleConfirmation.confirmCheckbox}
          confirmLabel={isRemovingUserId ? "Suppression..." : removeRoleConfirmation.confirmLabel}
          description={removeRoleConfirmation.description}
          eyebrow="Suppression"
          impactItems={removeRoleConfirmation.impactItems}
          isOpen
          onCancel={handleCancelRemoveRoleConfirmation}
          onClose={handleCancelRemoveRoleConfirmation}
          onConfirm={(confirmSharedIdentity) => {
            void handleConfirmRemoveRole(confirmSharedIdentity);
          }}
          summaryDensity={removeRoleConfirmation.summaryDensity}
          summaryItems={removeRoleConfirmation.summaryItems}
          title={removeRoleConfirmation.title}
          tone={removeRoleConfirmation.tone}
          warningMessage={removeRoleConfirmation.warningMessage}
        />
      ) : null}

      {roleAssignConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmLabel={(isSavingUserId || isManualSubmitting) ? "Application..." : roleAssignConfirmation.confirmLabel}
          description={roleAssignConfirmation.description}
          eyebrow="Attribution"
          impactItems={roleAssignConfirmation.impactItems}
          isOpen
          onCancel={handleCancelRoleAssignConfirmation}
          onClose={handleCancelRoleAssignConfirmation}
          onConfirm={() => {
            void handleConfirmRoleAssign();
          }}
          summaryDensity={roleAssignConfirmation.summaryDensity}
          summaryItems={roleAssignConfirmation.summaryItems}
          title={roleAssignConfirmation.title}
          tone={roleAssignConfirmation.tone}
        />
      ) : null}

      {suspendAccessConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmCheckbox={suspendAccessConfirmation.confirmCheckbox}
          confirmLabel={isTogglingDriverPortalUserId
            ? (suspendAccessConfirmation.action === "reactivate" ? "Réactivation..." : "Suspension...")
            : suspendAccessConfirmation.confirmLabel}
          description={suspendAccessConfirmation.description}
          eyebrow={suspendAccessConfirmation.action === "reactivate" ? "Réactivation" : "Suspension"}
          impactItems={suspendAccessConfirmation.impactItems}
          isOpen
          onCancel={handleCancelSuspendAccessConfirmation}
          onClose={handleCancelSuspendAccessConfirmation}
          onConfirm={(confirmSharedIdentity) => {
            void handleConfirmSuspendAccess(confirmSharedIdentity);
          }}
          summaryDensity={suspendAccessConfirmation.summaryDensity}
          summaryItems={suspendAccessConfirmation.summaryItems}
          title={suspendAccessConfirmation.title}
          tone={suspendAccessConfirmation.tone}
          warningMessage={suspendAccessConfirmation.warningMessage}
        />
      ) : null}

      {roleSaveConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmLabel={isSavingRole ? "Enregistrement..." : roleSaveConfirmation.confirmLabel}
          description={roleSaveConfirmation.description}
          eyebrow="Confirmation"
          impactItems={roleSaveConfirmation.impactItems}
          onCancel={handleCancelRoleSaveConfirmation}
          onClose={handleCancelRoleSaveConfirmation}
          onConfirm={() => {
            void handleConfirmRoleSave();
          }}
          summaryItems={roleSaveConfirmation.summaryItems}
          title={roleSaveConfirmation.title}
          tone={roleSaveConfirmation.tone}
        />
      ) : null}

      {passwordChangeConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmLabel={isUpdatingPasswordUserId ? "Enregistrement..." : passwordChangeConfirmation.confirmLabel}
          description={passwordChangeConfirmation.description}
          eyebrow="Mot de passe"
          impactItems={passwordChangeConfirmation.impactItems}
          onCancel={handleCancelPasswordChange}
          onClose={handleCancelPasswordChange}
          onConfirm={(payload) => {
            void handleConfirmPasswordChange(payload);
          }}
          summaryDensity={passwordChangeConfirmation.summaryDensity}
          summaryItems={passwordChangeConfirmation.summaryItems}
          textPrompt={passwordChangeConfirmation.textPrompt}
          title={passwordChangeConfirmation.title}
          tone={passwordChangeConfirmation.tone}
          warningMessage={passwordChangeConfirmation.warningMessage}
        />
      ) : null}

      {passwordResetEmailConfirmation ? (
        <ConfirmationModal
          cornerRadius="grain"
          confirmLabel={isSendingResetEmailUserId ? "Envoi..." : passwordResetEmailConfirmation.confirmLabel}
          description={passwordResetEmailConfirmation.description}
          eyebrow="Réinitialisation"
          impactItems={passwordResetEmailConfirmation.impactItems}
          onCancel={handleCancelPasswordResetEmail}
          onClose={handleCancelPasswordResetEmail}
          onConfirm={() => {
            void handleConfirmPasswordResetEmail();
          }}
          summaryDensity={passwordResetEmailConfirmation.summaryDensity}
          summaryItems={passwordResetEmailConfirmation.summaryItems}
          title={passwordResetEmailConfirmation.title}
          tone={passwordResetEmailConfirmation.tone}
          errorMessage={passwordResetEmailConfirmation.errorMessage}
          warningMessage={passwordResetEmailConfirmation.warningMessage}
        />
      ) : null}
    </>
  );
}
